import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Button,
  Flex,
  Typography,
  Divider,
  Dropdown,
  Card,
  Collapse,
  Form,
  Input,
  DatePicker,
  Checkbox,
  Upload,
  Image,
  message,
  GetProp,
  Modal,
} from "antd";
import {
  MoreOutlined,
  TruckOutlined,
  CloseCircleTwoTone,
  CheckCircleFilled,
  DownloadOutlined,
  DownOutlined,
  ExclamationCircleFilled,
  ArrowLeftOutlined,
} from "@ant-design/icons";
import type { UploadFile, UploadProps } from "antd/es/upload/interface";
import { Document, Page, pdfjs } from "react-pdf";

// Configurar el worker de pdf.js usando CDN
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

type FileType = Parameters<GetProp<UploadProps, "beforeUpload">>[0];
import { axiosInstance, formatPrice, nombreDeEntrega } from "@/utilities";
import { API_URL_ORDERS, API_URL_SHIPMENT } from "@/services/XCONS_ENV";
import noImage from "@/assets/img/no-img.png";
import envioImage from "@/assets/img/img-envio.png";
import corteImage from "@/assets/img/corte.png";
import cantoImage from "@/assets/img/cantos.png";
import { DeliveriesSteps } from "./steps";
import dayjs from "dayjs";
import {
  DiscountPrice,
  getUnitFinalInclFromDescription,
} from "../discount-icon";
import { useTranslation } from "react-i18next";
import { Larrow } from "../icon/Larrow";

const { Title, Text, Paragraph } = Typography;

type ShipmentsProps = {
  shipment: any;
  productsMap: Record<string, any>;
  statusDelivery: string[];
  invoice?: string;
  updateOrderData?: () => void;
  canEnableDelivery?: boolean;
  orderPayment?: any;
  discountAmount?: number;
  interests?: number;
  additionalPayment?: number;
  orderId?: number;
  allShipments?: any;
  optimizations?: any[];
  orderItems?: any[];
  /** URL base de la tienda Magento del vendedor. Se usa para construir la
   *  URL de descarga de etiqueta Zippin cuando magentoShipmentId está disponible. */
  storeLink?: string;
  creditMemos?: any[];
  /** True cuando OTRA entrega del mismo order está en edit/cancel mode —
   *  esta se renderiza con opacity 70% y sin interactividad. */
  isDimmed?: boolean;
  /** Notifica al wrapper que ESTA entrega entró en modo edit (move o cancel)
   *  para que las demás se dimmen. */
  onEnterEditMode?: () => void;
  /** Notifica al wrapper que ESTA entrega salió del modo edit. */
  onExitEditMode?: () => void;
};

export const OrderShipmentItem: React.FC<ShipmentsProps> = ({
  shipment,
  productsMap,
  statusDelivery,
  invoice,
  updateOrderData,
  canEnableDelivery,
  orderPayment,
  interests = 0,
  additionalPayment = 0,
  discountAmount = 0,
  orderId,
  allShipments,
  optimizations = [],
  orderItems = [],
  storeLink,
  creditMemos,
  isDimmed,
  onEnterEditMode,
  onExitEditMode,
}) => {
  const { t } = useTranslation();
  const [fileList, setFileList] = useState<any[]>([]);
  const [invoiceFileList, setInvoiceFileList] = useState<any[]>([]);
  const [loadingStep, setLoadingStep] = useState(false);
  const [isSelectMode, setIsSelectMode] = useState(false);
  const [selectedItems, setSelectedItems] = useState<any[]>([]);
  const [editableQuantities, setEditableQuantities] = useState<
    Record<string, number>
  >({});
  // En isSelectMode el operador puede marcar el envío para incluirlo en la
  // cancelación junto con los items. Si es true, el shipping se suma al
  // grandTotal y se considera para detectar full-cancel.
  const [cancelShipping, setCancelShipping] = useState(false);
  const [previewImage, setPreviewImage] = useState("");
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewPdf, setPreviewPdf] = useState("");
  const [previewPdfOpen, setPreviewPdfOpen] = useState(false);
  const [numPages, setNumPages] = useState<number | null>(null);
  const [previewFilename, setPreviewFilename] = useState("");
  const [showCancelPreview, setShowCancelPreview] = useState(false);
  const [cancellationRequested, setCancellationRequested] = useState(false);
  // Descarga de la etiqueta de Zippin (PDF) — el backend la proxya desde la
  // API de Zippin con las credenciales del vendor.
  const [zippinLabelDownloading, setZippinLabelDownloading] = useState(false);
  // Marca que el próximo cambio de status (reject del request) debe volver a la
  // etapa de selección en vez de salir del modo cancel. Lo lee el effect de
  // reset por cambio de status.
  const returnToSelectRef = useRef(false);
  // ---- Move-products mode (XCONS-1929) -------------------------------------
  // Permite mover items de este shipment a otro (existente o nuevo) o
  // combinar dos entregas (mueve el 100% y elimina el origen). El flujo es
  // disjunto del select/cancel mode existente; ambos no pueden estar activos
  // al mismo tiempo.
  const [isMoveMode, setIsMoveMode] = useState(false);
  const [moveQtyByItemId, setMoveQtyByItemId] = useState<Record<number, number>>({});
  const [moveTarget, setMoveTarget] = useState<
    | { mode: "existing"; shipmentId: number; label: string }
    | { mode: "new" }
    | null
  >(null);
  const [moveLoading, setMoveLoading] = useState(false);
  const [moveConfirmOpen, setMoveConfirmOpen] = useState(false);

  // ---- Edición de delivery date + questions (en modo "Editar entrega") -----
  // Drafts locales seedeados desde shipment.metadata; se persisten on
  // blur/change vía PATCH /shipments/:id (deep-merge de metadata).
  const [ddDraft, setDdDraft] = useState<{
    day?: string;
    from?: string;
    to?: string;
  }>({});
  const [questionDrafts, setQuestionDrafts] = useState<
    { label: string; value: string }[]
  >([]);

  const [form] = Form.useForm();

  const N = (v: any, d = 0) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : d;
  };
  const toNum = (v: any, d = 0) => (Number.isFinite(Number(v)) ? Number(v) : d);
  const cents = (v: number) => Math.round((v + Number.EPSILON) * 100) / 100;

  // ------------ Upload helpers
  const getBase64 = (file: FileType): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(error);
    });

  const handlePreview = async (file: UploadFile) => {
    // Detectar si es PDF usando tipo MIME o extensión
    const isPdf =
      file.type === "application/pdf" ||
      file.name?.toLowerCase().endsWith(".pdf") ||
      (file as any).type?.includes("pdf");

    let fileUrl = file.url;

    if (!fileUrl && file.originFileObj) {
      if (isPdf) {
        // Para PDFs, crear un blob URL
        fileUrl = URL.createObjectURL(file.originFileObj);
      } else {
        // Para imágenes, usar base64
        fileUrl = await getBase64(file.originFileObj as FileType);
      }
    }

    // Agregar #toolbar=0 para PDFs para evitar descarga automática
    if (isPdf && fileUrl && !fileUrl.includes("#")) {
      fileUrl = `${fileUrl}#toolbar=0`;
    }

    if (isPdf) {
      setPreviewPdf(fileUrl || "");
      setPreviewFilename(file.name || "documento.pdf");
      setPreviewPdfOpen(true);
    } else {
      setPreviewImage(fileUrl || "");
      setPreviewOpen(true);
    }
  };

  const handleBeforeUpload = (file: FileType) => {
    // Puedes cambiar el '15' por la cantidad de Megabytes máximos que desees permitir
    const limitMB = 15;
    const isLessThanLimit = file.size / 1024 / 1024 < limitMB;

    if (!isLessThanLimit) {
      message.error(
        `El archivo "${file.name}" excede el tamaño máximo permitido de ${limitMB}MB`,
      );
    }

    return isLessThanLimit ? true : Upload.LIST_IGNORE;
  };

  const handleRemoveFile = (file: any) => {
    setFileList((prev) => prev.filter((f) => f.uid !== file.uid));
  };

  const handleRemoveInvoiceFile = (file: any) => {
    setInvoiceFileList((prev) => prev.filter((f) => f.uid !== file.uid));
  };

  const handleRemovePackingSlip = (file: any): Promise<boolean> => {
    return new Promise((resolve) => {
      Modal.confirm({
        title: t("orders.packingSlip.delete.confirm.title"),
        content: t("orders.packingSlip.delete.confirm.content"),
        okText: t("buttons.confirm"),
        cancelText: t("buttons.back"),
        okButtonProps: { danger: true },
        onOk: async () => {
          try {
            await axiosInstance.delete(
              `${API_URL_SHIPMENT}/packing-slips/${file.uid}`,
            );
            setInvoiceFileList((prev: any[]) =>
              prev.filter((f) => f.uid !== file.uid),
            );
            message.success(t("orders.packingSlip.delete.success"));
            updateOrderData?.();
            resolve(true);
          } catch (error: any) {
            if (error.message === "Insufficient permissions") {
              message.error(t("insufficient.permissions"));
            } else {
              message.error(t("orders.packingSlip.delete.failed"));
            }
            resolve(false);
          }
        },
        onCancel: () => resolve(false),
      });
    });
  };

  const handleRemoveProofOfDelivery = (file: any): Promise<boolean> => {
    return new Promise((resolve) => {
      Modal.confirm({
        title: t("orders.proofOfDelivery.delete.confirm.title"),
        content: t("orders.proofOfDelivery.delete.confirm.content"),
        okText: t("buttons.confirm"),
        cancelText: t("buttons.back"),
        okButtonProps: { danger: true },
        onOk: async () => {
          try {
            await axiosInstance.delete(
              `${API_URL_SHIPMENT}/proofs-of-delivery/${file.uid}`,
            );
            setFileList((prev: any[]) =>
              prev.filter((f) => f.uid !== file.uid),
            );
            message.success(t("orders.proofOfDelivery.delete.success"));
            updateOrderData?.();
            resolve(true);
          } catch (error: any) {
            if (error.message === "Insufficient permissions") {
              message.error(t("insufficient.permissions"));
            } else {
              message.error(t("orders.proofOfDelivery.delete.failed"));
            }
            resolve(false);
          }
        },
        onCancel: () => resolve(false),
      });
    });
  };

  function parseAdjustmentsFromDescription(item: any) {
    try {
      const raw = item?.description;
      if (!raw) return [];
      const arr = Array.isArray(raw) ? raw : JSON.parse(raw);
      return (arr ?? [])
        .filter((a: any) => a?.adjustmentType === "discount")
        .map((a: any) => ({
          name: a?.name,
          valueType: a?.valueType as "fixed" | "percent",
          value: Number(a?.value) || 0,
        }));
    } catch {
      return [];
    }
  }

  /** Devuelve el precio unitario FINAL c/IVA aplicando los descuentos de `description`. */
  function computeFinalUnitInclFromDescription(product: any): number {
    const taxPct = toNum(product?.taxPercent, 0);
    const taxFactor = 1 + taxPct / 100;

    // Precio unitario base CON IVA (pre-descuentos de description)
    const unitBaseIncl = cents(
      toNum(
        product?.priceInclTax ??
        (product?.price != null ? toNum(product.price) * taxFactor : 0),
        0,
      ),
    );

    const adjs = parseAdjustmentsFromDescription(product);
    if (!adjs.length) return unitBaseIncl;

    let unitTotalDiscIncl = 0;
    for (const a of adjs) {
      if (!a.value) continue;
      if (a.valueType === "percent") {
        unitTotalDiscIncl += cents(unitBaseIncl * (a.value / 100));
      } else if (a.valueType === "fixed") {
        // viene sin IVA -> llevar a CON IVA
        unitTotalDiscIncl += cents(a.value * taxFactor);
      }
    }

    return Math.max(0, cents(unitBaseIncl - unitTotalDiscIncl));
  }

  // Seleccionar / deseleccionar todos (checkbox del header)
  const handleSelectAll = (checked: boolean) => {
    if (!shipment?.items?.length) return;

    if (checked) {
      const selectable = (shipment.items || []).filter(
        (it: any) => Number(it.qty) > 0,
      );
      setSelectedItems(selectable);
      const next: Record<string, number> = {};
      selectable.forEach((it: any) => (next[it.orderItemId] = it.qty ?? 0));
      setEditableQuantities(next);
      // Al marcar "Seleccionar todos" también se incluye el envío.
      setCancelShipping(true);
    } else {
      setSelectedItems([]);
      setEditableQuantities({});
      setCancelShipping(false);
    }
  };

  const handleItemCheck = (item: any, checked: boolean) => {
    setSelectedItems((prev) =>
      checked
        ? [...prev, item]
        : prev.filter((i) => i.orderItemId !== item.orderItemId),
    );
    setEditableQuantities((prev) => {
      const next = { ...prev };
      if (checked) next[item.orderItemId] = item.qty ?? 0;
      else delete next[item.orderItemId];
      return next;
    });
  };

  const handleEnterSelectMode = () => {
    setIsSelectMode(true);
    setSelectedItems([]);
    setEditableQuantities({});
    setCancelShipping(false);
  };

  const resetCancellationState = () => {
    setIsSelectMode(false);
    setSelectedItems([]);
    setEditableQuantities({});
    setCancelShipping(false);
  };

  // ---- Move-products handlers (XCONS-1929) ---------------------------------
  const handleEnterMoveMode = () => {
    // Mutex with cancel mode
    setIsSelectMode(false);
    setSelectedItems([]);
    setEditableQuantities({});
    setCancelShipping(false);
    setShowCancelPreview(false);
    setCancellationRequested(false);

    setIsMoveMode(true);
    setMoveQtyByItemId({});
    setMoveTarget(null);
    setMoveConfirmOpen(false);
  };

  const handleExitMoveMode = () => {
    setIsMoveMode(false);
    setMoveQtyByItemId({});
    setMoveTarget(null);
    setMoveConfirmOpen(false);
  };

  const handleMoveQtyChange = (orderItemId: number, qty: number, maxQty: number) => {
    const safe = Math.max(0, Math.min(Number.isFinite(qty) ? qty : 0, maxQty));
    setMoveQtyByItemId((prev) => {
      const next = { ...prev };
      if (safe === 0) delete next[orderItemId];
      else next[orderItemId] = safe;
      return next;
    });
  };

  const handleMoveSelectAll = (checked: boolean) => {
    if (!checked) {
      setMoveQtyByItemId({});
      return;
    }
    const next: Record<number, number> = {};
    (shipment?.items ?? []).forEach((it: any) => {
      const q = Number(it.qty) || 0;
      if (q > 0) next[it.orderItemId] = q;
    });
    setMoveQtyByItemId(next);
  };

  const moveItemRowState = (item: any) => {
    const total = Number(item?.qty) || 0;
    const move = Number(moveQtyByItemId[item?.orderItemId] || 0);
    const keep = Math.max(0, total - move);
    let checkState: { checked: boolean; indeterminate: boolean };
    if (move <= 0) checkState = { checked: false, indeterminate: false };
    else if (move >= total) checkState = { checked: true, indeterminate: false };
    else checkState = { checked: false, indeterminate: true };
    return { total, move, keep, ...checkState };
  };

  const hasAnyMove = useMemo(
    () => Object.values(moveQtyByItemId).some((q) => Number(q) > 0),
    [moveQtyByItemId],
  );

  // hay items seleccionados para cancelar (cualquier qty > 0 dispara
  // "Request cancellation" enabled).
  const hasAnyCancel = useMemo(
    () =>
      Object.values(editableQuantities).some((q) => Number(q) > 0) ||
      cancelShipping,
    [editableQuantities, cancelShipping],
  );

  // Persiste el "Request cancellation" en backend vía el endpoint dedicado
  // `POST /shipments/:id/cancel-request`. Ese endpoint usa authz híbrido
  // (customer-owner OR operator con order_shipment Editor) y marca el
  // shipment como `pending_cancel` con la selección guardada en
  // `metadata.cancelRequest`. La solicitud vive sobre reload — otro
  // operador (o el mismo customer que la creó desde Magento) entra y la
  // confirma o rechaza después.
  const handleRequestCancellation = async () => {
    if (!shipment?.shipmentId) return;
    const itemsToCancel = (shipment?.items ?? [])
      .map((it: any) => ({
        itemId: Number(it.orderItemId ?? it.itemId),
        qty: Number(
          editableQuantities[Number(it.orderItemId ?? it.itemId)] || 0,
        ),
      }))
      .filter((x: any) => x.qty > 0);
    if (!itemsToCancel.length && !cancelShipping) {
      message.warning("No hay items seleccionados para cancelar.");
      return;
    }
    setLoadingStep(true);
    try {
      await axiosInstance.post(
        `${API_URL_SHIPMENT}/${shipment.shipmentId}/cancel-request`,
        {
          itemsToCancel,
          grandTotal: cancelTotalInclTax,
          cancelShipping,
        },
      );
      setShowCancelPreview(true);
      message.success("Solicitud de cancelación registrada");
      updateOrderData?.();
    } catch (e: any) {
      message.error(
        e?.response?.data?.message ||
          e?.message ||
          "Error al registrar la solicitud",
      );
    } finally {
      setLoadingStep(false);
    }
  };

  // Rechaza un cancel request persistido: vuelve el shipment al status
  // previo (`metadata.originalStatus`) y borra `metadata.cancelRequest`.
  const handleRejectCancelRequest = async () => {
    if (!shipment?.shipmentId) return;
    const prevMeta = (shipment?.metadata as any) ?? {};
    const targetStatus = prevMeta.originalStatus || "pending";
    const { cancelRequest, originalStatus, ...restMeta } = prevMeta;
    setLoadingStep(true);
    try {
      await axiosInstance.patch(`${API_URL_SHIPMENT}/${shipment.shipmentId}`, {
        status: targetStatus,
        metadata: restMeta,
      });
      resetCancellationState();
      message.success("Solicitud de cancelación rechazada");
      updateOrderData?.();
    } catch (e: any) {
      message.error(
        e?.response?.data?.message ||
          "Error al rechazar la solicitud de cancelación",
      );
    } finally {
      setLoadingStep(false);
    }
  };

  // "Back" desde el preview de cancelación: revierte el request persistido
  // (status → original, limpia metadata.cancelRequest) PERO vuelve a la etapa
  // de selección manteniendo la selección, en vez de salir del modo cancel.
  const handleBackFromCancelPreview = async () => {
    if (!shipment?.shipmentId) return;
    // Si todavía no se persistió (no es pending_cancel), Back es local.
    if (shipment?.status !== "pending_cancel") {
      setShowCancelPreview(false);
      return;
    }
    const prevMeta = (shipment?.metadata as any) ?? {};
    const targetStatus = prevMeta.originalStatus || "pending";
    const { cancelRequest, originalStatus, ...restMeta } = prevMeta;
    returnToSelectRef.current = true;
    setLoadingStep(true);
    try {
      await axiosInstance.patch(`${API_URL_SHIPMENT}/${shipment.shipmentId}`, {
        status: targetStatus,
        metadata: restMeta,
      });
      setShowCancelPreview(false);
      updateOrderData?.();
    } catch (e: any) {
      returnToSelectRef.current = false;
      message.error(
        e?.response?.data?.message ||
          "Error al volver a la selección",
      );
    } finally {
      setLoadingStep(false);
    }
  };

  // Otros shipments del mismo order, candidatos a destino (no canceled, no
  // este mismo). Para el dropdown "Move to".
  const moveTargetCandidates = useMemo(() => {
    const list = Array.isArray(allShipments) ? allShipments : [];
    return list.filter(
      (s: any) =>
        s?.shipmentId !== shipment?.shipmentId &&
        String(s?.status).toLowerCase() !== "canceled",
    );
  }, [allShipments, shipment?.shipmentId]);

  // Número ordinal de la entrega dentro del order (1-based). Usa
  // metadata.deliveryGroup si está, sino cae al index en allShipments
  // ordenado por shipmentId.
  const deliveryNumber = useMemo(() => {
    const explicit = Number((shipment as any)?.metadata?.deliveryGroup);
    if (explicit > 0) return explicit;
    const list = Array.isArray(allShipments) ? [...allShipments] : [];
    list.sort((a: any, b: any) => (a?.shipmentId ?? 0) - (b?.shipmentId ?? 0));
    const idx = list.findIndex(
      (s: any) => s?.shipmentId === shipment?.shipmentId,
    );
    return idx >= 0 ? idx + 1 : 1;
  }, [allShipments, shipment?.shipmentId, (shipment as any)?.metadata?.deliveryGroup]);

  // Número ordinal (1-based) de CUALQUIER entrega dentro del order. Misma
  // regla que `deliveryNumber` pero para un shipment arbitrario — necesario
  // para etiquetar los destinos del "Move to" con su posición real (no el
  // índice dentro de la lista filtrada de candidatos, que excluye la entrega
  // actual y desfasaba la numeración).
  const deliveryNumberOf = (s: any): number => {
    const explicit = Number(s?.metadata?.deliveryGroup);
    if (explicit > 0) return explicit;
    const list = Array.isArray(allShipments) ? [...allShipments] : [];
    list.sort((a: any, b: any) => (a?.shipmentId ?? 0) - (b?.shipmentId ?? 0));
    const idx = list.findIndex((x: any) => x?.shipmentId === s?.shipmentId);
    return idx >= 0 ? idx + 1 : 1;
  };

  const handleConfirmMove = async (targetOverride?: typeof moveTarget) => {
    if (!orderId || !shipment?.shipmentId) return;
    const target = targetOverride ?? moveTarget;
    if (!target) return;

    const items = Object.entries(moveQtyByItemId)
      .map(([itemId, qty]) => ({
        itemId: Number(itemId),
        qty: Number(qty) || 0,
      }))
      .filter((it) => it.qty > 0);
    if (items.length === 0) return;

    const payload: any = {
      sourceShipmentId: shipment.shipmentId,
      target:
        target.mode === "existing"
          ? { mode: "existing", shipmentId: target.shipmentId }
          : { mode: "new" },
      items,
    };

    setMoveLoading(true);
    try {
      // Crea la solicitud (no ejecuta el move). El source pasa a
      // pending_move y un operador debe confirmar/rechazar después.
      await axiosInstance.post(
        `${API_URL_ORDERS}/${orderId}/shipments/move-request`,
        payload,
      );
      message.success("Solicitud de movimiento registrada");
      handleExitMoveMode();
      updateOrderData?.();
    } catch (e: any) {
      const status = e?.response?.status;
      const apiMsg =
        e?.response?.data?.message || e?.message || "Error al mover productos";
      if (status === 409) {
        message.error(apiMsg);
      } else {
        message.error(apiMsg);
      }
    } finally {
      setMoveLoading(false);
      setMoveConfirmOpen(false);
    }
  };

  // Confirm de una solicitud pending_move. El backend restaura el
  // originalStatus del source y ejecuta el move.
  const handleConfirmMoveRequest = async () => {
    if (!orderId || !shipment?.shipmentId) return;
    setLoadingStep(true);
    try {
      await axiosInstance.post(
        `${API_URL_ORDERS}/${orderId}/shipments/move-confirm`,
        { sourceShipmentId: shipment.shipmentId },
      );
      message.success("Movimiento confirmado");
      updateOrderData?.();
    } catch (e: any) {
      message.error(
        e?.response?.data?.message ||
          e?.message ||
          "Error al confirmar el movimiento",
      );
    } finally {
      setLoadingStep(false);
    }
  };

  // Reject de una solicitud pending_move. Restaura el originalStatus del
  // source y limpia metadata.moveRequest.
  const handleRejectMoveRequest = async () => {
    if (!orderId || !shipment?.shipmentId) return;
    setLoadingStep(true);
    try {
      await axiosInstance.post(
        `${API_URL_ORDERS}/${orderId}/shipments/move-reject`,
        { sourceShipmentId: shipment.shipmentId },
      );
      message.success("Solicitud de movimiento rechazada");
      updateOrderData?.();
    } catch (e: any) {
      message.error(
        e?.response?.data?.message ||
          e?.message ||
          "Error al rechazar la solicitud de movimiento",
      );
    } finally {
      setLoadingStep(false);
    }
  };

  // "Back" desde el preview de pending_move: revierte la solicitud (move-reject)
  // PERO reconstruye el estado de edición (target + cantidades desde
  // metadata.moveRequest) para volver al paso anterior (preview in-session con
  // "Confirm movement"), en vez de reiniciar todo el flujo.
  const handleBackFromMoveRequest = async () => {
    if (!orderId || !shipment?.shipmentId) return;
    const moveReq = (shipment?.metadata as any)?.moveRequest;
    setLoadingStep(true);
    try {
      await axiosInstance.post(
        `${API_URL_ORDERS}/${orderId}/shipments/move-reject`,
        { sourceShipmentId: shipment.shipmentId },
      );
      // Reconstruir la edición de move desde la solicitud que se acaba de revertir.
      if (moveReq) {
        const qtyMap: Record<number, number> = {};
        for (const it of moveReq.items ?? []) {
          const id = Number(it.itemId);
          const qty = Number(it.qty) || 0;
          if (id > 0 && qty > 0) qtyMap[id] = qty;
        }
        let target: typeof moveTarget = null;
        if (moveReq.target?.mode === "existing") {
          const tgt = (allShipments ?? []).find(
            (s: any) => s?.shipmentId === moveReq.target?.shipmentId,
          );
          target = {
            mode: "existing",
            shipmentId: moveReq.target.shipmentId,
            label: `Delivery ${tgt ? deliveryNumberOf(tgt) : ""}`.trim(),
          };
        } else if (moveReq.target?.mode === "new") {
          target = { mode: "new" };
        }
        setMoveQtyByItemId(qtyMap);
        setMoveTarget(target);
        setIsMoveMode(true);
      }
      updateOrderData?.();
    } catch (e: any) {
      message.error(
        e?.response?.data?.message ||
          e?.message ||
          "Error al volver al paso anterior",
      );
    } finally {
      setLoadingStep(false);
    }
  };

  // Seed de los drafts de delivery date + questions desde la metadata del
  // shipment (al cambiar de shipment o tras guardar/refetch).
  useEffect(() => {
    const dd = (shipment?.metadata as any)?.deliveryDate;
    setDdDraft(
      dd ? { day: dd.day, from: dd.from, to: dd.to } : {},
    );
    const opts = (shipment?.metadata as any)?.shippingExtraInfo?.options;
    setQuestionDrafts(
      Array.isArray(opts)
        ? opts.map((o: any) => ({ label: o?.label, value: o?.value ?? "" }))
        : [],
    );
  }, [shipment?.shipmentId, shipment?.metadata]);

  // Persiste el delivery date editado (merge con el existente: conserva method/label).
  const saveDeliveryDate = async (next: {
    day?: string;
    from?: string;
    to?: string;
  }) => {
    if (!shipment?.shipmentId) return;
    try {
      await axiosInstance.patch(
        `${API_URL_SHIPMENT}/${shipment.shipmentId}/delivery-info`,
        { deliveryDate: next },
      );
      updateOrderData?.();
    } catch (e: any) {
      message.error(
        e?.response?.data?.message || "Error al guardar la fecha de entrega",
      );
    }
  };

  // Persiste las respuestas editadas (reemplaza options, conserva title).
  const saveQuestions = async (
    options: { label: string; value: string }[],
  ) => {
    if (!shipment?.shipmentId) return;
    try {
      await axiosInstance.patch(
        `${API_URL_SHIPMENT}/${shipment.shipmentId}/delivery-info`,
        { shippingExtraInfo: { options } },
      );
      updateOrderData?.();
    } catch (e: any) {
      message.error(
        e?.response?.data?.message || "Error al guardar las respuestas",
      );
    }
  };

  const processedItems = useMemo(() => {
    return selectedItems
      .map((item) => {
        const editQty = editableQuantities[item.orderItemId];
        if (editQty === undefined) return null;
        if (editQty > item.qty) return { ...item, qty: item.qty };
        return editQty > 0 ? { ...item, qty: editQty } : null;
      })
      .filter(Boolean);
  }, [selectedItems, editableQuantities]);

  // Packing Slip Upload
  const handlePackingUpload = async () => {
    if (invoiceFileList.length === 0) {
      message.warning("Por favor seleccione un archivo primero");
      return;
    }
    const file = invoiceFileList[invoiceFileList.length - 1].originFileObj;
    if (!file) return;

    const values = form.getFieldsValue(["packing_slip_number", "packing_slip"]);
    try {
      const formData = new FormData();
      formData.append("file", file);
      if (values.packing_slip_number) formData.append("pointOfSale", values.packing_slip_number);
      if (values.packing_slip) formData.append("number", values.packing_slip);

      await axiosInstance.post(
        `${API_URL_SHIPMENT}/${shipment.shipmentId}/packing-slips`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      message.success(t("common.messages.packingSlipUploaded"));
      updateOrderData?.();
    } catch (error) {
      message.error(t("common.messages.packingSlipUploadFailed"));
    }
  };

  // (Re)envía el remito ya cargado a Magento, SIN cambiar el estado del envío.
  const handleResendPackingSlip = async () => {
    if (!shipment?.shipmentId) return;
    setLoadingStep(true);
    try {
      await axiosInstance.post(
        `${API_URL_SHIPMENT}/${shipment.shipmentId}/packing-slips/resend`,
      );
      message.success(t("orders.packingSlip.resendSuccess"));
      updateOrderData?.();
    } catch (e: any) {
      message.error(
        e?.response?.data?.message || t("orders.packingSlip.resendError"),
      );
    } finally {
      setLoadingStep(false);
    }
  };

  // Proof of Delivery Upload
  const handleProofUpload = async () => {
    if (fileList.length === 0) {
      message.warning("Por favor seleccione un archivo primero");
      return;
    }
    const file = fileList[fileList.length - 1].originFileObj;
    if (!file) return;

    try {
      const formData = new FormData();
      formData.append("file", file);

      await axiosInstance.post(
        `${API_URL_SHIPMENT}/${shipment.shipmentId}/proofs-of-delivery`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      message.success(t("common.messages.proofOfDeliveryUploaded"));
      updateOrderData?.();
    } catch (error) {
      message.error(t("common.messages.proofOfDeliveryUploadFailed"));
    }
  };

  // Datos de Zippin del shipment (los guarda el sync listener de la API en
  // metadata.zippin cuando la entrega se creó con Zippin): shipmentId (id en
  // Zippin), trackingUrl, carrierTrackingId y carrierName.
  const zippinData = (shipment?.metadata as any)?.zippin;

  const handleDownloadZippinLabel = async () => {
    if (!shipment?.shipmentId) return;
    setZippinLabelDownloading(true);
    try {
      const res = await axiosInstance.get(
        `${API_URL_SHIPMENT}/${shipment.shipmentId}/zippin-label`,
        { responseType: "blob" },
      );
      const url = URL.createObjectURL(
        new Blob([res.data], { type: "application/pdf" }),
      );
      const link = document.createElement("a");
      link.href = url;
      link.download = `zippin-label-${zippinData?.shipmentId ?? shipment.shipmentId}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (e: any) {
      if (e.message === "Insufficient permissions") {
        message.error(t("insufficient.permissions"));
      } else if (e?.response?.status === 409) {
        message.warning("La etiqueta aún no está disponible. Intente nuevamente en unos minutos.");
      } else {
        message.error(
          e?.response?.data?.message ||
            "Error al descargar la etiqueta de envío",
        );
      }
    } finally {
      setZippinLabelDownloading(false);
    }
  };

  // Si querés auto-cargar archivos ya existentes (packing/proof)
  const getPackingSlipNumber = async (packing: {
    packingSlipId: number;
    originalFilename: string;
  }) => {
    try {
      const res = await axiosInstance.get(
        `${API_URL_SHIPMENT}/packing-slips/${packing.packingSlipId}`,
        { responseType: "blob" },
      );
      const isPdf = packing.originalFilename?.toLowerCase().endsWith(".pdf");
      const mimeType = isPdf ? "application/pdf" : (res.data.type || "image/jpeg");
      const blob = new Blob([res.data], { type: mimeType });
      const url = URL.createObjectURL(blob);
      setInvoiceFileList([
        {
          uid: packing.packingSlipId.toString(),
          name: packing.originalFilename || "packing_slip",
          status: "done",
          url,
          thumbUrl: url,
          type: mimeType,
          originFileObj: new File([blob], packing.originalFilename, {
            type: mimeType,
          }),
        },
      ]);
      setPreviewImage(url);
    } catch (error) {
      message.error(t("common.messages.packingSlipLoadFailed"));
    }
  };

  // Corrige punto de venta y número del remito ya cargado, sin volver a subir
  // el PDF (xcons-docs#346). Se llama on blur de cada input.
  //
  // Antes los dos campos quedaban `disabled` apenas el remito tenía números:
  // un dedazo obligaba a borrar el documento para rehacerlo.
  //
  // El backend decide: con el remito ya publicado en Magento contesta 409 —su
  // `ship_name` lleva el número— y ahí se muestra su mensaje y el campo vuelve
  // al valor guardado.
  const savePackingSlipNumbers = async () => {
    const packingSlip = shipment?.packingSlip;
    if (!packingSlip?.packingSlipId) return;

    const v = form.getFieldsValue(["packing_slip_number", "packing_slip"]);
    const pointOfSale = (v.packing_slip_number ?? "").toString().trim();
    const number = (v.packing_slip ?? "").toString().trim();

    const sinCambios =
      pointOfSale === ((packingSlip.pointOfSale ?? "") as string) &&
      number === ((packingSlip.number ?? "") as string);
    if (sinCambios) return;

    if (!/^\d+$/.test(pointOfSale) || !/^\d+$/.test(number)) {
      message.error(t("orders.documents.numbersOnlyDigits"));
      return;
    }

    try {
      await axiosInstance.patch(
        `${API_URL_SHIPMENT}/packing-slips/${packingSlip.packingSlipId}`,
        { pointOfSale, number },
      );
      message.success(t("orders.documents.numbersSaved"));
      updateOrderData?.();
    } catch (error: any) {
      // El 409 manda `message` como string; el 400 de validación, como array.
      const detalle = error?.response?.data?.message;
      message.error(
        (Array.isArray(detalle) ? detalle.join(" ") : detalle) ||
          t("orders.documents.numbersFailed"),
      );
      form.setFieldsValue({
        packing_slip_number: packingSlip.pointOfSale ?? undefined,
        packing_slip: packingSlip.number ?? undefined,
      });
    }
  };

  const getProofOfDelivery = async (
    proofs: Array<{
      proofOfDeliveryId: number;
      originalFilename: string;
    }> = [],
  ) => {
    try {
      const files = await Promise.all(
        proofs.map(async (proof) => {
          const res = await axiosInstance.get(
            `${API_URL_SHIPMENT}/proofs-of-delivery/${proof.proofOfDeliveryId}`,
            { responseType: "blob" },
          );
          const isPdf = proof.originalFilename?.toLowerCase().endsWith(".pdf");
          const mimeType = isPdf ? "application/pdf" : (res.data.type || "image/jpeg");
          const blob = new Blob([res.data], { type: mimeType });
          const url = URL.createObjectURL(blob);
          return {
            uid: proof.proofOfDeliveryId.toString(),
            name: proof.originalFilename || "proof_of_delivery",
            status: "done",
            url,
            thumbUrl: url,
            type: mimeType,
            originFileObj: new File([blob], proof.originalFilename, {
              type: mimeType,
            }),
          };
        }),
      );
      setFileList(files);
    } catch (error) {
      message.error(t("common.messages.proofOfDeliveryLoadFailed"));
    }
  };

  const buildOriginalMap = (shipment: any) => {
    const map = new Map<number, number>();
    for (const it of shipment?.items ?? []) {
      const id = Number(it?.orderItemId ?? it?.itemId);
      const q = N(it?.qty);
      if (id > 0 && q > 0) map.set(id, q);
    }
    return map;
  };

  const normalizeItemsForPayload = (
    shipment: any,
    opts: any,
    baseItems: any[],
  ) => {
    const raw =
      opts && "itemsToCancel" in opts && opts.itemsToCancel?.length
        ? opts.itemsToCancel.map((it: any) => ({
          itemId: Number(it.orderItemId ?? it.itemId),
          qty: N(it.cancelQty),
        }))
        : baseItems;

    // filtrar inválidos y qty > 0
    return raw.filter((it: any) => it.itemId > 0 && N(it.qty) > 0);
  };

  const isFullCancelByQty = (
    shipment: any,
    itemsForPayload: Array<{ itemId: number; qty: number }>,
  ) => {
    const original = buildOriginalMap(shipment);
    if (!original.size) return false;

    for (const [id, origQty] of original.entries()) {
      const sel = N(itemsForPayload.find((x) => x.itemId === id)?.qty);
      if (!(sel >= origQty)) return false;
    }
    return itemsForPayload.length >= original.size;
  };

  /**
   * La nota de crédito de una entrega cancelada, para el atajo al final de su flujo. Es la que
   * canceló esta entrega, o —en una cancelación de varias entregas a la vez— la que incluye
   * su número de entrega.
   */
  const notaDeCreditoDe = (envio: any) => {
    if (envio?.status !== "canceled" || !creditMemos?.length) return undefined;
    const id = Number(envio?.shipmentId);
    const meta = (m: any) => {
      const raw = m?.metadata;
      if (typeof raw !== "string") return raw;
      try {
        return JSON.parse(raw);
      } catch {
        return null;
      }
    };
    const directa = creditMemos.find(
      (m: any) =>
        Number(m?.canceledShipmentId ?? meta(m)?.sourceShipmentId) === id,
    );
    if (directa) return directa;
    const grupo = envio?.metadata?.deliveryGroup;
    if (grupo == null) return undefined;
    return creditMemos.find((m: any) =>
      (meta(m)?.bulkDeliveryGroups ?? []).map(Number).includes(Number(grupo)),
    );
  };

  const handleChangeStep = async (
    status: string,
    shipment: any,
    third?: any,
  ) => {
    setLoadingStep(true);
    const { shipmentId, metadata, items = [], cancelledItems = [] } = shipment;

    const opts =
      typeof third === "number" ? { grandTotal: third } : third || {};
    const grandTotal = (opts as any)?.grandTotal;

    // Caso "solo envío": opts.cancelShipping === true + sin items en
    // opts.itemsToCancel. Va por endpoint dedicado que NO toca items ni
    // pasa el shipment a canceled (solo restaura el originalStatus + NC).
    const isShippingOnly =
      status === "canceled" &&
      (opts as any)?.cancelShipping === true &&
      (!Array.isArray((opts as any)?.itemsToCancel) ||
        (opts as any).itemsToCancel.length === 0);

    if (isShippingOnly) {
      try {
        await axiosInstance.post(
          `${API_URL_SHIPMENT}/${shipmentId}/cancel-shipping-confirm`,
        );
        message.success("Cancelación de envío aplicada.");
        updateOrderData?.();
        resetCancellationState();
        setCancellationRequested(false);
        return;
      } catch (e: any) {
        message.error(
          e?.response?.data?.message ||
            e?.message ||
            "Error al cancelar el envío",
        );
        return;
      } finally {
        setLoadingStep(false);
      }
    }

    // base cuando no viene selección explícita
    const baseItems = (items.length ? items : cancelledItems).map(
      (it: any) => ({
        itemId: Number(it.orderItemId ?? it.itemId),
        qty: N(it.qty),
      }),
    );

    // selección efectiva
    const itemsForPayload = normalizeItemsForPayload(shipment, opts, baseItems);

    if (!itemsForPayload.length) {
      message.warning("No hay ítems válidos para procesar.");
      setLoadingStep(false);
      return;
    }

    const full = isFullCancelByQty(shipment, itemsForPayload);

    try {
      // === PARCIAL: liberar qty del shipment origen y luego crear shipment "canceled" ===
      if (status === "canceled" && !full) {
        if (!orderId) throw new Error("orderId no disponible.");

        // 1) Reducir cantidades en el shipment origen (desasignar qty)
        const originalMap = buildOriginalMap(shipment);
        const remainingMap = new Map(originalMap); // clon

        for (const sel of itemsForPayload) {
          const orig = N(remainingMap.get(sel.itemId));
          if (orig <= 0)
            throw new Error(
              `Ítem ${sel.itemId} no pertenece al shipment actual.`,
            );
          const newQty = orig - N(sel.qty);
          if (newQty < 0)
            throw new Error(
              `Qty a cancelar (${sel.qty}) excede la qty en shipment (${orig}) para item ${sel.itemId}.`,
            );
          remainingMap.set(sel.itemId, newQty);
        }

        const sourceItemsAfter = Array.from(remainingMap.entries())
          .filter(([, q]) => q > 0)
          .map(([id, q]) => ({ itemId: id, qty: q }));

        const snapshotItems = (shipment?.items ?? []).map((it: any) => ({
          itemId: Number(it.orderItemId ?? it.itemId),
          qty: N(it.qty),
        }));

        // Capturar el status PREVIO del shipment origen para preservarlo en
        // el metadata del nuevo shipment canceled. Si el shipment estaba en
        // `pending_cancel` (request persistido), el "status previo real"
        // vive en `metadata.originalStatus` capturado al hacer Request.
        const srcOriginalStatus =
          (metadata as any)?.originalStatus || shipment?.status;
        // Status a restaurar en el shipment origen: si venía de
        // pending_cancel, recuperamos el original; si era cualquier otro,
        // se conserva tal cual.
        const sourceTargetStatus =
          shipment?.status === "pending_cancel"
            ? (metadata as any)?.originalStatus || "pending"
            : shipment?.status;
        // Limpiar metadata.cancelRequest y originalStatus del origen al
        // confirmarse (la solicitud ya está procesada).
        const {
          cancelRequest: _cr,
          originalStatus: _os,
          ...sourceMetaClean
        } = (metadata ?? {}) as any;

        await axiosInstance.patch(`${API_URL_SHIPMENT}/${shipmentId}`, {
          metadata: sourceMetaClean,
          items: sourceItemsAfter,
          status: sourceTargetStatus,
        });

        let createdShipmentId: number | undefined;

        try {
          //crear shipment "canceled" con las qty liberadas
          // Copiamos deliveryGroup (y name asociado) del shipment origen para
          // que el label del creditmemo pueda identificar a qué entrega
          // pertenece la cancelación parcial. Sin esto cae al fallback
          // histórico "Parcialmente cancelado N".
          const srcDeliveryGroup = (shipment as any)?.metadata?.deliveryGroup;
          const srcName = (shipment as any)?.metadata?.name;
          // Flag para que el sync a Magento incluya el ítem-envío en el
          // creditmemo además de los items normales (partial cancel con
          // envío). `opts.cancelShipping` viene del Modal.confirm onOk.
          const includeShippingFlag =
            (opts as any)?.cancelShipping === true;

          const postPayload: any = {
            deliveryMethod: "canceled",
            metadata: {
              ...(grandTotal != null ? { grandTotal: Number(grandTotal) } : {}),
              sourceShipmentId: shipmentId,
              cancelationType: "partial",
              ...(srcDeliveryGroup != null
                ? { deliveryGroup: srcDeliveryGroup }
                : {}),
              ...(srcName ? { name: srcName } : {}),
              ...(srcOriginalStatus
                ? { originalStatus: srcOriginalStatus }
                : {}),
              ...(includeShippingFlag ? { includeShipping: true } : {}),
            },
            items: itemsForPayload,
          };

          const { data: created } = await axiosInstance.post(
            `${API_URL_ORDERS}/${orderId}/shipments`,
            postPayload,
          );

          createdShipmentId =
            created?.shipmentId ??
            created?.id ??
            created?.data?.shipmentId ??
            created?.data?.id;

          // asegurar status "canceled" en el nuevo shipment (si el POST no lo setea)
          if (createdShipmentId) {
            await axiosInstance.patch(
              `${API_URL_SHIPMENT}/${createdShipmentId}`,
              {
                status: "canceled",
                metadata: postPayload.metadata,
                items: itemsForPayload,
              },
            );
          }

          message.success(
            "Cancelación parcial aplicada (shipment de cancelación creado).",
          );
          updateOrderData?.();
          resetCancellationState();
          setCancellationRequested(false);
          return Promise.resolve();
        } catch (inner) {
          // ROLLBACK: si falló el POST, restaurar qty del shipment origen
          await axiosInstance.patch(`${API_URL_SHIPMENT}/${shipmentId}`, {
            metadata: { ...(metadata ?? {}) },
            items: snapshotItems,
            status: shipment?.status,
          });
          throw inner;
        }
      }

      // === TOTAL (o cualquier otro status): patch al shipment actual
      // Capturamos el status previo para mostrarlo como "estado al momento
      // de la cancelación" en el módulo cancelado (spec XCONS-1929 etapa 5).
      // Si venía de pending_cancel, el status real previo está guardado en
      // metadata.originalStatus (lo seteamos al persistir el request).
      const srcOriginalStatusTotal =
        (metadata as any)?.originalStatus || shipment?.status;
      // Limpiar `cancelRequest` del metadata (la solicitud ya se confirmó).
      const { cancelRequest: _crTotal, ...metaWithoutRequest } =
        (metadata ?? {}) as any;
      const patchPayload: any = {
        metadata: {
          ...metaWithoutRequest,
          ...(grandTotal != null ? { grandTotal: Number(grandTotal) } : {}),
          cancelationType: "total",
          ...(srcOriginalStatusTotal && srcOriginalStatusTotal !== "canceled"
            ? { originalStatus: srcOriginalStatusTotal }
            : {}),
        },
        items: itemsForPayload,
        status,
      };

      if (!patchPayload.items.every((it: any) => it.itemId && N(it.qty) >= 0)) {
        throw new Error("Invalid item data in payload");
      }

      await axiosInstance.patch(
        `${API_URL_SHIPMENT}/${shipmentId}`,
        patchPayload,
      );
      updateOrderData?.();
      message.success(t("common.messages.shipmentUpdated"));
      return Promise.resolve();
    } catch (e: any) {
      console.error(e);
      message.error(e?.message || "Error al actualizar el shipment.");
      return Promise.reject(e);
    } finally {
      setLoadingStep(false);
      setCancellationRequested(false);
    }
  };

  useEffect(() => {
    // No cerrar el preview si el shipment está en `pending_cancel` —
    // la solicitud está persistida en backend y debe verse en etapa 3
    // (esperando confirmación o rechazo).
    if (shipment?.status === "pending_cancel") return;
    if (!isSelectMode || processedItems.length === 0)
      setShowCancelPreview(false);
  }, [isSelectMode, processedItems.length, shipment?.status]);

  // Hidratación: cuando llega un shipment con status `pending_cancel`,
  // auto-entrar a modo cancel + etapa 3 reconstruyendo la selección desde
  // `metadata.cancelRequest`. Esto cubre tanto reload del operador como
  // customers que solicitaron desde Magento. Incluye el caso de solicitud
  // "solo envío" (itemsToCancel vacío + cancelShipping=true).
  useEffect(() => {
    if (shipment?.status !== "pending_cancel") return;
    const req = (shipment?.metadata as any)?.cancelRequest;
    if (!req) return;
    const hasItems = (req.itemsToCancel?.length ?? 0) > 0;
    const wantsShipping = req.cancelShipping === true;
    if (!hasItems && !wantsShipping) return;
    setIsSelectMode(true);
    setShowCancelPreview(true);
    setCancelShipping(wantsShipping);
    const editable: Record<number, number> = {};
    const selected: any[] = [];
    for (const ic of req.itemsToCancel ?? []) {
      const id = Number(ic.itemId);
      const qty = Number(ic.qty) || 0;
      if (id > 0 && qty > 0) {
        editable[id] = qty;
        const match = (shipment?.items ?? []).find(
          (x: any) => Number(x.orderItemId ?? x.itemId) === id,
        );
        if (match) selected.push(match);
      }
    }
    setEditableQuantities(editable);
    setSelectedItems(selected);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shipment?.shipmentId, shipment?.status]);

  const qtyToCancelByItemId = useMemo(() => {
    const m: Record<string, number> = {};
    for (const it of processedItems) {
      const q = Number(it?.qty) || 0;
      if (q > 0 && it?.orderItemId) m[it.orderItemId] = q;
    }
    return m;
  }, [processedItems]);

  // construir items del panel de cancelación con datos de producto
  const cancelItems = useMemo(() => {
    return processedItems.map((it: any) => ({
      ...it,
      product: productsMap?.[it.orderItemId] ?? null,
    }));
  }, [processedItems, productsMap]);

  const itemsForOriginalPanel = useMemo(() => {
    const baseItems = shipment?.items || [];

    const withLiveRemaining = baseItems.map((it: any) => {
      const original = Number(it.qty) || 0;

      const isSelected = selectedItems.some(
        (s) => Number(s.orderItemId) === Number(it.orderItemId),
      );
      const edited =
        isSelectMode && isSelected
          ? Number(editableQuantities[it.orderItemId] ?? 0)
          : 0;

      const toCancel = Math.max(0, Math.min(original, edited));
      const remaining = Math.max(0, original - toCancel);

      return {
        ...it,
        product: productsMap?.[it.orderItemId] || null,
        displayQty: isSelectMode ? remaining : original,
      };
    });

    return showCancelPreview
      ? withLiveRemaining.filter((it: any) => it.displayQty > 0)
      : withLiveRemaining;
  }, [
    shipment?.items,
    productsMap,
    isSelectMode,
    selectedItems,
    editableQuantities,
    showCancelPreview,
  ]);

  // Ítems realmente cancelables (qty > 0)
  const selectableItemsCount = useMemo(
    () =>
      (shipment?.items || []).filter((it: any) => Number(it.qty) > 0).length,
    [shipment?.items],
  );

  useEffect(() => {
    // Solo si hay packingSlip o proofsOfDelivery, los cargamos
    if (shipment?.packingSlip) {
      getPackingSlipNumber(shipment.packingSlip);
    } else {
      setInvoiceFileList([]);
    }

    if (shipment?.proofsOfDelivery) {
      getProofOfDelivery(shipment.proofsOfDelivery);
    } else {
      setFileList([]);
    }
  }, [shipment, invoice]);

  // Memoizar las opciones del PDF para evitar recargas innecesarias
  const pdfOptions = useMemo(() => ({
    cMapUrl: `https://unpkg.com/pdfjs-dist@${pdfjs.version}/cmaps/`,
    cMapPacked: true,
    standardFontDataUrl: `https://unpkg.com/pdfjs-dist@${pdfjs.version}/standard_fonts/`,
  }), []);

  const canSendReadyToDeliver = useMemo(() => {
    const hasPacking = Boolean(shipment?.packingSlip);
    return hasPacking;
  }, [shipment?.packingSlip]);

  const canSendReadyToDelivered = useMemo(() => {
    const hasProofs = (shipment?.proofsOfDelivery?.length ?? 0) > 0;
    return hasProofs;
  }, [shipment?.proofsOfDelivery]);

  useEffect(() => {
    // Packing slip existente -> cargar archivo y prellenar form
    if (shipment?.packingSlip) {
      getPackingSlipNumber(shipment.packingSlip);
      form.setFieldsValue({
        packing_slip_number: shipment.packingSlip.pointOfSale ?? undefined,
        packing_slip: shipment.packingSlip.number ?? undefined,
      });
    } else {
      setInvoiceFileList([]);
      form.resetFields(["packing_slip_number", "packing_slip"]);
    }

    // Proofs existentes
    if (shipment?.proofsOfDelivery?.length) {
      getProofOfDelivery(shipment.proofsOfDelivery);
    } else {
      setFileList([]);
    }
  }, [shipment, form]);

  // Total de productos del panel original (usa displayQty)
  const productsTotal = (itemsForOriginalPanel || []).reduce(
    (acc: number, it: any) => {
      const prod = productsMap?.[it.orderItemId];
      const qty = Number(it.displayQty ?? it.qty) || 0;
      if (!prod || !qty) return acc;

      const unitFinalIncl = getUnitFinalInclFromDescription(prod);
      return acc + unitFinalIncl * qty;
    },
    0,
  );

  // total cancelado SOLO de productos (unitario con IVA * qty seleccionada)
  const cancelProductsTotalInclTax = useMemo(() => {
    return cancelItems.reduce((acc: number, it: any) => {
      const prod = productsMap?.[it.orderItemId];
      if (!prod) return acc;
      const unitFinalIncl = getUnitFinalInclFromDescription(prod);
      return acc + unitFinalIncl * Number(it.qty ?? 0);
    }, 0);
  }, [cancelItems, productsMap]);

  // cancelación TOTAL real del envío: cada línea con cancelQty >= qty original
  const isFullCancelForCredit = useMemo(() => {
    const all = shipment?.items || [];
    if (!isSelectMode || !showCancelPreview || all.length === 0) return false;
    return all.every((it: any) => {
      const original = Number(it.qty) || 0;
      if (original <= 0) return true;
      const selected = qtyToCancelByItemId[it.orderItemId] || 0;
      return selected >= original;
    });
  }, [shipment?.items, qtyToCancelByItemId, isSelectMode, showCancelPreview]);

  // total de cancelación (productos + envío si fue marcado por el operador
  // o si es cancelación total de productos)
  const cancelTotalInclTax = useMemo(() => {
    const includeShipping = cancelShipping || isFullCancelForCredit;
    const shippingIncl = includeShipping ? N(shipment?.priceInclTax) : 0;
    return cents(cancelProductsTotalInclTax + shippingIncl);
  }, [
    cancelProductsTotalInclTax,
    isFullCancelForCredit,
    cancelShipping,
    shipment?.priceInclTax,
  ]);

  const remainingQty = useMemo(() => {
    const items = shipment?.items ?? [];
    const total = items.reduce(
      (acc: number, it: any) => acc + (Number(it.qty) || 0),
      0,
    );

    if (!isSelectMode) return total;

    const cancelMap: Record<number, number> =
      typeof qtyToCancelByItemId === "object" && qtyToCancelByItemId !== null
        ? qtyToCancelByItemId
        : Object.fromEntries(
          (cancelItems ?? []).map((ci: any) => [
            Number(ci.orderItemId ?? ci.itemId),
            Number(ci.qty) || 0,
          ]),
        );

    // qty total seleccionada a cancelar (acotada al qty original)
    const selectedToCancel = items.reduce((acc: number, it: any) => {
      const orig = Number(it.qty) || 0;
      const sel = Math.min(orig, Number(cancelMap[it.orderItemId]) || 0);
      return acc + sel;
    }, 0);

    return Math.max(0, total - selectedToCancel);
  }, [shipment?.items, isSelectMode, cancelItems, qtyToCancelByItemId]);

  // Si salís de select-mode o te quedás sin selección, cerrá el preview.
  // No tocar si el shipment está en pending_cancel (la solicitud está
  // persistida y debe seguir visible en etapa 3).
  useEffect(() => {
    if (shipment?.status === "pending_cancel") return;
    if (!isSelectMode || processedItems.length === 0)
      setShowCancelPreview(false);
  }, [isSelectMode, processedItems.length, shipment?.status]);

  // Si cambia el shipment (accordion) o su status, reseteá selección y
  // salí de cualquier modo de edición. Si el nuevo shipment está en
  // pending_cancel, el otro useEffect de hidratación se encarga de poblar
  // selección desde metadata.
  useEffect(() => {
    if (shipment?.status === "pending_cancel") return;
    // Si venimos de un "Back" desde el preview (reject del request), no salimos
    // del modo cancel: volvemos a la etapa de selección con la selección intacta.
    if (returnToSelectRef.current) {
      returnToSelectRef.current = false;
      setShowCancelPreview(false);
      setIsSelectMode(true);
      setCancellationRequested(false);
      return;
    }
    setSelectedItems([]);
    setEditableQuantities({});
    setShowCancelPreview(false);
    setIsSelectMode(false);
    setCancellationRequested(false);
  }, [shipment?.shipmentId, shipment?.status]);

  // El shipping queda cancelado cuando se confirmó una solicitud de
  // "solo envío" (el shipment vuelve a su originalStatus pero la metadata
  // guarda historial). Cualquier entry en shippingCancelHistory marca el
  // envío como ya cancelado para mostrarlo así en el render normal.
  const isShippingCanceled = useMemo(() => {
    const hist = (shipment?.metadata as any)?.shippingCancelHistory;
    return Array.isArray(hist) && hist.length > 0;
  }, [shipment?.metadata]);

  // shippingTotal = 0 cuando:
  //   - el envío ya fue cancelado (NC procesada en pasado), o
  //   - estamos en modo select sin preview con el envío marcado para
  //     cancelar (preview previo a hacer Request).
  const shippingTotal =
    isShippingCanceled ||
    (isSelectMode && !showCancelPreview && cancelShipping)
      ? 0
      : N(shipment?.priceInclTax);

  const grandTotal = cents(
    productsTotal +
    shippingTotal +
    N(interests) +
    N(additionalPayment) +
    N(discountAmount),
  );

  const shipmentTotalInclTax_products = cents(productsTotal + shippingTotal);

  const shippingName = nombreDeEntrega(shipment?.metadata?.name) || "Shipping";
  const shippingPriceInclTax = Number(shipment?.priceInclTax || 0);

  /**
   * Builds a map: productId (string) → { corteItem, cantoItem }
   * For each optimization, main_product_id is the board product.
   * The optimization lists service productIds in its `items` array.
   * We identify corte/canto service items by their mainCatalog SKU prefix.
   */
  const optimizationServiceMap = useMemo(() => {
    const map: Record<string, { corteItem: any | null; corteQty: number; cantoItem: any | null; cantoQty: number; cantoFisicoItem: any | null; cantoFisicoQty: number; downloadUrls: Record<string, string[]> }> = {};

    if (!optimizations?.length || !orderItems?.length) return map;

    // Build a lookup from productId → order item. With the multi-DG model
    // (Phase 1, may-2026) the same productId can appear on several
    // orderItems in different delivery groups; here we still default to the
    // first match per productId because the optimization JSON does not
    // carry the deliveryGroup of its parent board, and there is no link
    // back from optimization → orderItem in the current schema.
    // TODO multi-DG-optimizer: when an `optimizer_id` column is added to
    // `sales_order_item` (or extracted from product_options), index by that
    // instead so two boards of the same product on different DGs each get
    // their own service attribution.
    const orderItemByProductId: Record<number, any> = {};
    for (const oi of orderItems) {
      if (oi.productId != null && orderItemByProductId[Number(oi.productId)] == null) {
        orderItemByProductId[Number(oi.productId)] = oi;
      }
    }

    for (const opt of optimizations) {
      const mainProductId = opt.main_product_id;
      if (!mainProductId) continue;

      // The optimization lists which productIds are service items and their qty for THIS optimization
      const optItemsList: { product_id: number; qty: number }[] = (opt.items ?? []).map((i: any) => ({
        product_id: Number(i.product_id),
        qty: Number(i.qty) || 0,
      }));

      let corteItem: any = null;
      let corteQty = 0;
      let cantoItem: any = null;
      let cantoQty = 0;
      let cantoFisicoItem: any = null;
      let cantoFisicoQty = 0;

      for (const optItem of optItemsList) {
        // Skip the main board product
        if (optItem.product_id === Number(mainProductId)) continue;
        const oi = orderItemByProductId[optItem.product_id];
        if (!oi) continue;
        const sku: string = oi.linkedProduct?.variations?.[0]?.mainCatalog?.sku ?? "";
        if (sku.startsWith("SERVICIO-CORETQ") || sku.includes("CORTE")) {
          corteItem = oi;
          corteQty = optItem.qty;
        } else if (sku.startsWith("SERVICIO-PEGCANT") || sku.includes("PEGADO")) {
          cantoItem = oi;
          cantoQty = optItem.qty;
        } else if (optItem.qty > 0) {
          // Physical canto ABS tape (e.g. EGGER-H3430ST22-04-23)
          cantoFisicoItem = oi;
          cantoFisicoQty = optItem.qty;
        }
      }

      // Build per-optimization download URLs grouped by extension
      const planilla = opt.api_json?.planilla ?? {};
      const downloadUrls: Record<string, string[]> = {};
      for (const url of ([planilla.url_planos, planilla.url_cnc, planilla.url_aux_cnc, planilla.url_labels] as string[]).filter(Boolean)) {
        const raw = url.split("?")[0].split("/").pop() ?? "";
        const ext = raw.includes(".") ? raw.split(".").pop()!.toLowerCase() : "file";
        if (!downloadUrls[ext]) downloadUrls[ext] = [];
        if (!downloadUrls[ext].includes(url)) downloadUrls[ext].push(url);
      }

      // Only add entry if at least one service exists
      if (corteItem || cantoItem || cantoFisicoItem) {
        map[String(mainProductId)] = { corteItem, corteQty, cantoItem, cantoQty, cantoFisicoItem, cantoFisicoQty, downloadUrls };
      }
    }

    return map;
  }, [optimizations, orderItems]);

  /**
   * Collects all download URLs from optimizations tied to this shipment,
   * grouped by file extension (pdf, saw, ptx, …).
   */
  /**
   * Sum ALL service/accessory items that belong to optimizations for this shipment
   * but are NOT included in shipment.items (they are never physically dispatched).
   * This includes:
   *   - SERVICIO-CORETQ (cut service)
   *   - SERVICIO-PEGCANT (edge banding service)
   *   - Canto ABS products (physical edge tapes linked to the optimization)
   *
   * We identify these by checking, for each optimization whose main_product_id is in
   * this shipment, all opt.items[] that are NOT the main board product itself.
   */
  const servicesTotal = useMemo(() => {
    if (!optimizations?.length || !orderItems?.length) return 0;

    // Set of productIds that ARE in shipment.items (already counted in productsTotal)
    const shipmentProductIds = new Set<number>(
      (shipment?.items ?? [])
        .map((it: any) => productsMap?.[it.orderItemId]?.productId)
        .filter((id: any) => id != null)
        .map(Number),
    );

    // Build lookup from productId → order item (from the full order).
    // See multi-DG note in optimizationServiceMap above — same caveat applies
    // here for multi-DG + optimizer attribution.
    const orderItemByProductId: Record<number, any> = {};
    for (const oi of orderItems) {
      if (oi.productId != null && orderItemByProductId[Number(oi.productId)] == null) {
        orderItemByProductId[Number(oi.productId)] = oi;
      }
    }

    let total = 0;
    for (const opt of optimizations) {
      const mainPid = Number(opt.main_product_id);
      // Only process optimizations whose board is in this shipment
      if (!shipmentProductIds.has(mainPid)) continue;

      for (const optItem of (opt.items ?? [])) {
        const pid = Number(optItem.product_id);
        const qty = Number(optItem.qty) || 0;
        if (!qty) continue;
        // Skip the main board product (already in shipment.items → productsTotal)
        if (pid === mainPid) continue;
        // Skip items already in shipment.items
        if (shipmentProductIds.has(pid)) continue;

        const oi = orderItemByProductId[pid];
        if (!oi) continue;

        total += getUnitFinalInclFromDescription(oi) * qty;
      }
    }
    return cents(total);
  }, [optimizations, orderItems, shipment?.items, productsMap]);

  const shipmentTotalInclTax = cents(shipmentTotalInclTax_products + servicesTotal);

  const downloadGroupsByExt = useMemo(() => {
    if (!optimizations?.length) return {} as Record<string, string[]>;

    const shipmentProductIds = new Set<number>(
      (shipment?.items ?? [])
        .map((it: any) => productsMap?.[it.orderItemId]?.productId)
        .filter((id: any) => id != null)
        .map(Number),
    );

    const extMap: Record<string, string[]> = {};

    for (const opt of optimizations) {
      const mainPid = Number(opt.main_product_id);
      if (!shipmentProductIds.has(mainPid)) continue;

      const planilla = opt.api_json?.planilla ?? {};
      const urlFields: string[] = [
        planilla.url_planos,
        planilla.url_cnc,
        planilla.url_aux_cnc,
        planilla.url_labels,
      ].filter(Boolean);

      for (const url of urlFields) {
        const raw = url.split("?")[0].split("/").pop() ?? "";
        const ext = raw.includes(".") ? raw.split(".").pop()!.toLowerCase() : "file";
        if (!extMap[ext]) extMap[ext] = [];
        if (!extMap[ext].includes(url)) extMap[ext].push(url);
      }
    }

    return extMap;
  }, [optimizations, shipment?.items, productsMap]);

  const hasDownloads = Object.keys(downloadGroupsByExt).length > 0;

  const handleDownloadGroup = (urls: string[]) => {
    urls.forEach((url, idx) => {
      setTimeout(() => {
        const a = document.createElement("a");
        a.href = url;
        a.download = url.split("/").pop() ?? "download";
        a.target = "_blank";
        a.rel = "noopener noreferrer";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      }, idx * 300);
    });
  };

  const selectable = (shipment.items || []).filter(
    (it: any) => Number(it.qty) > 0,
  );
  // "Todos seleccionados" = todos los items + el envío. Si el envío no está
  // marcado, queda en indeterminate.
  const allSelected =
    selectedItems.length === selectable.length &&
    selectable.length > 0 &&
    cancelShipping;

  const shippingIncludedForCredit = isFullCancelForCredit || cancelShipping;

  // El 2-bloques de cancel preview vive dentro del wrapper {showOriginalPanel && ...}.
  // La lista normal de items ya se auto-oculta vía display:none cuando hay preview,
  // así que no hace falta ocultar el wrapper completo en full cancel — hacerlo
  // tiraba también el 2-bloques (bug: en total cancel se ocultaba "Products to be cancelled").
  const showOriginalPanel = true;

  const sourceShipment = useMemo(() => {
    const sid = Number(shipment?.metadata?.sourceShipmentId);
    if (!sid || !Array.isArray(allShipments)) return null;
    return allShipments.find((s) => Number(s?.shipmentId) === sid) || null;
  }, [shipment?.metadata?.sourceShipmentId, allShipments]);

  const effectiveMeta = useMemo(() => {
    const mine = shipment?.metadata ?? {};
    const from = sourceShipment?.metadata ?? {};
    const isCanceled =
      String(shipment?.status).toLowerCase() === "canceled" ||
      String(shipment?.deliveryMethod).toLowerCase() === "canceled";

    return {
      shippingTypeId: mine.shippingTypeId ?? from.shippingTypeId,
      shippingTypeLabel: mine.shippingTypeLabel ?? from.shippingTypeLabel,

      name:
        mine.name ??
        (isCanceled
          ? `Partial cancellation${from?.name
            ? ` — ${from.name}`
            : sourceShipment?.shipmentId
              ? ` — Shipment #${sourceShipment.shipmentId}`
              : ""
          }`
          : from.name),
    };
  }, [
    shipment?.status,
    shipment?.deliveryMethod,
    shipment?.metadata,
    sourceShipment,
  ]);

  const isCanceledStatus =
    String(shipment?.status).toLowerCase() === "canceled";

  const isTotalCancellation = useMemo(() => {
    if (!isCanceledStatus) return false;

    if (!sourceShipment) return true;
    const remainingQty = (sourceShipment?.items ?? []).reduce(
      (acc: number, it: any) => acc + (Number(it?.qty) || 0),
      0,
    );
    return remainingQty === 0;
  }, [isCanceledStatus, sourceShipment]);

  const showShippingLine = !isCanceledStatus || isTotalCancellation;

  console.log("shipment", shipment);

  // Sincroniza con el wrapper para dimear OTRAS entregas cuando ESTA está
  // en modo edición (move o cancel). El wrapper mantiene `editingShipmentId`
  // y pasa `isDimmed` a las demás.
  useEffect(() => {
    if (isMoveMode || isSelectMode) {
      onEnterEditMode?.();
    } else {
      onExitEditMode?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMoveMode, isSelectMode]);

  return (
    <>
      <Collapse
        style={{
          marginBottom: 16,
          ...(isDimmed
            ? { opacity: 0.7, pointerEvents: "none" as const }
            : {}),
        }}
        defaultActiveKey={[String(shipment.shipmentId)]}
        className={`${shipment?.status === "canceled"
          ? "custom-header-collapse-canceled"
          : "custom-header-collapse"
          }`}
        items={[
          {
            key: String(shipment.shipmentId),
            label: (
              <Flex gap={8} align="center">
                {/* Sin relleno a la derecha: el ⋮ va contra el borde del encabezado, que ya
                    tiene el suyo (Diseño, 2026-10-19). */}
                <Flex
                  justify="space-between"
                  align="center"
                  style={{
                    borderRadius: 8,
                    padding: "0 0 0 16px",
                    width: "100%",
                  }}
                >
                  <Flex align="center">
                    <Flex gap={8}>
                      {isSelectMode && (
                        <Checkbox
                          style={{ marginRight: 8 }}
                          checked={allSelected}
                          disabled={cancellationRequested}
                          indeterminate={
                            selectedItems.length > 0 && !allSelected
                          }
                          onChange={(e) => {
                            e.stopPropagation();
                            handleSelectAll(e.target.checked);
                          }}
                          onClick={(e) => e.stopPropagation()}
                        />
                      )}
                      <TruckOutlined style={{ color: "white", fontSize: 16 }} />
                      <Text strong style={{ color: "white" }}>
                        {isSelectMode
                          ? t("orders.partialCancelTitle", {
                              number: deliveryNumber,
                              defaultValue: `Cancelación parcial de Entrega ${deliveryNumber}`,
                            })
                          : shipment?.metadata?.shippingTypeId ||
                            effectiveMeta.shippingTypeId}
                      </Text>
                    </Flex>
                    {!isSelectMode && (
                      <>
                        <Divider
                          type="vertical"
                          style={{ backgroundColor: "white", height: 16 }}
                        />
                        <Flex gap={8} align="center">
                          <Text strong style={{ color: "white" }}>
                            {shipment?.metadata?.cancelationType === "partial"
                              ? t("orders.shipment.partialCancellation")
                              : nombreDeEntrega(
                                  shipment?.metadata?.name || effectiveMeta?.name,
                                )}
                          </Text>
                          {!canEnableDelivery &&
                            shipment?.status !== "canceled" && (
                              <Text italic style={{ color: "#FF8F1F" }}>
                                {t("payment.must.be.completed")}
                              </Text>
                            )}
                        </Flex>
                      </>
                    )}
                  </Flex>

                  {shipment?.status === "delivered" && (
                    <Flex>
                      <CheckCircleFilled style={{ transform: "scale(1.5)" }} />
                    </Flex>
                  )}

                  {shipment?.status === "canceled" && (
                    <Flex>
                      <CloseCircleTwoTone
                        twoToneColor={"red"}
                        style={{ transform: "scale(1.5)" }}
                      />
                    </Flex>
                  )}

                  {(shipment?.status === "pending" ||
                    shipment?.status === "preparing") &&
                    canEnableDelivery &&
                    // Ocultar el menú ⋮ mientras se mueven productos entre
                    // entregas (move) o se está en cancelación parcial (select):
                    // ya se eligió una opción del menú y el flujo se controla
                    // con los botones del header (Volver / Request / Confirm).
                    !isMoveMode &&
                    !isSelectMode && (
                      <Dropdown
                        trigger={["click"]}
                        menu={{
                          items: [
                            {
                              key: "1",
                              label: isMoveMode
                                ? t("buttom.Cancel.edit.delivery") ?? "Cancelar edición"
                                : t("buttom.Edit.delivery"),
                              // Editar = mover items entre entregas. Bloquea si:
                              //   - el shipment está post-in_transit (status>=3),
                              //   - ya fue sincronizado a Magento (magento_shipment_id),
                              //   - o estamos en select/cancel mode.
                              disabled:
                                statusDelivery.indexOf(shipment.status) >= 3 ||
                                Boolean(shipment?.magentoShipmentId) ||
                                isSelectMode,
                              onClick: (e) => {
                                e.domEvent.stopPropagation();
                                if (isMoveMode) {
                                  handleExitMoveMode();
                                } else {
                                  handleEnterMoveMode();
                                }
                              },
                            },
                            {
                              key: "2",
                              label: (
                                <Text style={{ color: "red" }}>
                                  {isSelectMode
                                    ? !showCancelPreview
                                      ? t("orders.tag.cancelrequest")
                                      : t("buttom.Return.request.cancellation")
                                    : t("button.cancelproducts")}
                                </Text>
                              ),
                              disabled:
                                statusDelivery.indexOf(shipment.status) >= 3 ||
                                isMoveMode,
                              onClick: (e) => {
                                e.domEvent.stopPropagation();

                                if (isSelectMode && showCancelPreview) {
                                  setShowCancelPreview(false);
                                  setCancellationRequested(false);
                                  return;
                                }

                                if (isSelectMode) {
                                  resetCancellationState();
                                  setCancellationRequested(false);
                                  return;
                                }

                                handleEnterSelectMode();
                              },
                            },
                          ],
                          onClick: (e) => e.domEvent.stopPropagation(),
                        }}
                      >
                        <Button
                          type="link"
                          icon={<MoreOutlined />}
                          onClick={(e) => e.stopPropagation()}
                          style={{
                            color: "white",
                            paddingInline: 0,
                            minWidth: 0,
                            width: "auto",
                          }}
                        />
                      </Dropdown>
                    )}
                </Flex>
              </Flex>
            ),
            children: (
              <Flex vertical gap={4}>
                {/* MOVE MODE HEADER — reemplaza el state flow */}
                {isMoveMode && (
                  <Flex
                    align="center"
                    justify="space-between"
                    gap={8}
                    style={{ padding: "12px 0" }}
                  >
                    <Button
                      icon={<ArrowLeftOutlined />}
                      // Si ya se eligió destino, Back vuelve al paso anterior
                      // (selección de destino) manteniendo las cantidades; si no,
                      // sale del modo move.
                      onClick={
                        moveTarget
                          ? () => setMoveTarget(null)
                          : handleExitMoveMode
                      }
                      disabled={moveLoading}
                    >
                      {t("buttom.back") ?? "Back"}
                    </Button>
                    {moveTarget ? (
                      <Button
                        type="primary"
                        loading={moveLoading}
                        onClick={() => setMoveConfirmOpen(true)}
                      >
                        {t("orders.shipment.confirmMovementTo", {
                          target:
                            moveTarget.mode === "existing"
                              ? moveTarget.label
                              : t("orders.shipment.newDelivery"),
                        })}
                      </Button>
                    ) : (
                      <Dropdown
                        trigger={["click"]}
                        disabled={!hasAnyMove}
                        menu={{
                          items: [
                            ...moveTargetCandidates.map((s: any) => {
                              const num = deliveryNumberOf(s);
                              return {
                                key: `existing-${s.shipmentId}`,
                                label: `Delivery ${num}${s?.metadata?.name ? " — " + nombreDeEntrega(s.metadata.name) : s?.deliveryMethod ? " — " + s.deliveryMethod : ""}`,
                                onClick: (e: any) => {
                                  e.domEvent.stopPropagation();
                                  // Crear la solicitud al instante (sin paso de
                                  // confirmación intermedio).
                                  const target = {
                                    mode: "existing" as const,
                                    shipmentId: s.shipmentId,
                                    label: `Delivery ${num}`,
                                  };
                                  setMoveTarget(target);
                                  handleConfirmMove(target);
                                },
                              };
                            }),
                            { type: "divider" as const, key: "div" },
                            {
                              key: "new",
                              label: t("orders.shipment.newDelivery"),
                              onClick: (e: any) => {
                                e.domEvent.stopPropagation();
                                const target = { mode: "new" as const };
                                setMoveTarget(target);
                                handleConfirmMove(target);
                              },
                            },
                          ],
                        }}
                      >
                        <Button disabled={!hasAnyMove}>
                          {t("orders.shipment.moveTo")} <DownOutlined />
                        </Button>
                      </Dropdown>
                    )}
                  </Flex>
                )}

                {/* CANCEL MODE HEADER — reemplaza el state flow cuando
                    se entra al modo "Cancel products". Etapa 2 muestra
                    Back / Request cancellation; etapa 3 (preview) muestra
                    Back / Confirm cancellation. */}
                {isSelectMode && (
                  <Flex
                    align="center"
                    justify="space-between"
                    gap={8}
                    style={{ padding: "12px 0" }}
                  >
                    {!showCancelPreview ? (
                      <>
                        <Button
                          icon={<ArrowLeftOutlined />}
                          onClick={() => {
                            resetCancellationState();
                            setCancellationRequested(false);
                          }}
                          disabled={loadingStep}
                        >
                          {t("buttom.back") ?? "Back"}
                        </Button>
                        <Button
                          danger
                          disabled={!hasAnyCancel || loadingStep}
                          loading={loadingStep}
                          onClick={handleRequestCancellation}
                        >
                          {t("orders.shipment.requestCancellation")}
                        </Button>
                      </>
                    ) : (
                      <>
                        {shipment?.status === "pending_cancel" ? (
                          <Button
                            icon={<ArrowLeftOutlined />}
                            onClick={handleBackFromCancelPreview}
                            disabled={loadingStep}
                            loading={loadingStep}
                          >
                            {t("buttom.back") ?? "Back"}
                          </Button>
                        ) : (
                          <Button
                            onClick={() => {
                              setShowCancelPreview(false);
                              setCancellationRequested(false);
                            }}
                            disabled={loadingStep}
                          >
                            {t("buttom.back") ?? "Back"}
                          </Button>
                        )}
                        <Button
                          type="primary"
                          danger
                          loading={loadingStep}
                          onClick={() => {
                            // Construir la selección parcial desde
                            // `editableQuantities` para pasarla como
                            // `opts.itemsToCancel` a handleChangeStep. Sin
                            // esto cae al fallback que toma todos los items
                            // con qty total → isFullCancelByQty=true →
                            // ejecuta branch TOTAL en lugar de PARTIAL.
                            const itemsToCancel = (shipment?.items ?? [])
                              .map((it: any) => ({
                                orderItemId: Number(
                                  it.orderItemId ?? it.itemId,
                                ),
                                cancelQty: Number(
                                  editableQuantities[
                                    Number(it.orderItemId ?? it.itemId)
                                  ] || 0,
                                ),
                              }))
                              .filter((x: any) => x.cancelQty > 0);
                            if (!itemsToCancel.length && !cancelShipping) {
                              message.warning(
                                "No hay items seleccionados para cancelar.",
                              );
                              return;
                            }
                            Modal.confirm({
                              title: t("orders.partialCancel.confirm.title"),
                              content: t(
                                "orders.partialCancel.confirm.content",
                              ),
                              okText: t("orders.partialCancel.confirm.okText"),
                              cancelText: t("buttom.back") ?? "Back",
                              cancelButtonProps: {
                                icon: <ArrowLeftOutlined />,
                              },
                              okButtonProps: { danger: true },
                              onOk: async () => {
                                try {
                                  await handleChangeStep("canceled", shipment, {
                                    itemsToCancel,
                                    grandTotal: cancelTotalInclTax,
                                    cancelShipping,
                                  });
                                } catch {
                                  // handleChangeStep ya muestra error
                                }
                              },
                            });
                          }}
                        >
                          {t("orders.shipment.confirmCancellation")}
                        </Button>
                      </>
                    )}
                  </Flex>
                )}

                {/* HEADER BOTONES PARA PENDING_MOVE — preview de
                    solicitud pendiente. Reject restaura originalStatus,
                    Confirm ejecuta el move usando metadata.moveRequest. */}
                {shipment?.status === "pending_move" && (
                  <Flex
                    align="center"
                    justify="space-between"
                    gap={8}
                    style={{ padding: "12px 0" }}
                  >
                    <Button
                      icon={<ArrowLeftOutlined />}
                      onClick={handleBackFromMoveRequest}
                      disabled={loadingStep}
                      loading={loadingStep}
                    >
                      {t("buttom.back") ?? "Back"}
                    </Button>
                    <Button
                      type="primary"
                      onClick={() => {
                        Modal.confirm({
                          title: t("orders.move.confirm.title"),
                          content: t("orders.move.confirm.content"),
                          okText: t("orders.move.confirm.okText"),
                          cancelText: t("buttom.back") ?? "Back",
                          icon: (
                            <ExclamationCircleFilled
                              style={{ color: "#faad14" }}
                            />
                          ),
                          cancelButtonProps: { icon: <ArrowLeftOutlined /> },
                          // Modal.confirm imperativo no hereda el theme del
                          // ConfigProvider, así que forzamos el verde de marca
                          // (xconsTheme.colorPrimary = #15744b).
                          okButtonProps: {
                            style: {
                              backgroundColor: "#15744b",
                              borderColor: "#15744b",
                            },
                          },
                          onOk: handleConfirmMoveRequest,
                        });
                      }}
                      disabled={loadingStep}
                      loading={loadingStep}
                    >
                      {t("orders.shipment.confirmMovement")}
                    </Button>
                  </Flex>
                )}

                {/* STEPS — sólo en modo normal (sin move, sin cancel, sin pending_move) */}
                {!isMoveMode && !isSelectMode && shipment?.status !== "pending_move" && (
                <DeliveriesSteps
                  notaDeCredito={notaDeCreditoDe(shipment)}
                  orderPayment={orderPayment}
                  processedItems={processedItems}
                  isSelectMode={isSelectMode}
                  currentStep={statusDelivery.indexOf(shipment.status)}
                  canSendReadyToDeliver={canSendReadyToDeliver}
                  shipment={shipment}
                  handleChangeStep={handleChangeStep}
                  invoice={invoice}
                  deliveryMethod={shipment?.deliveryMethod}
                  canEnableDelivery={canEnableDelivery}
                  onRequestCancellation={() => setShowCancelPreview(true)}
                  showCancelPreview={showCancelPreview}
                  cancelTotalInclTax={cancelTotalInclTax}
                  cancelationRequested={cancellationRequested}
                  setCancelationRequested={setCancellationRequested}
                />
                )}

                {/* PANEL DE CANCELACIÓN (preview legacy) — REMOVIDO.
                    Reemplazado por las 2 cards ricas (Products to be
                    cancelled / Products that are kept) renderizadas más
                    abajo. El bloque viejo está deshabilitado con `false`
                    pero se deja el código por si necesitamos recuperarlo
                    rápido durante el rollout. */}
                {false &&
                  isSelectMode &&
                  showCancelPreview &&
                  processedItems.length > 0 && (
                    <Collapse
                      defaultActiveKey={[`cancel-${shipment.shipmentId}`]}
                      className="custom-header-collapse-white"
                      style={{ margin: "16px 0" }}
                      items={[
                        {
                          key: `cancel-${shipment.shipmentId}`,
                          label: (
                            <Flex align="center" justify="space-between">
                              <Flex align="center">
                                <Flex gap={8}>
                                  <TruckOutlined
                                    style={{ color: "white", fontSize: 16 }}
                                  />

                                  <Text strong>
                                    {shipment?.metadata?.shippingTypeId ||
                                      effectiveMeta.shippingTypeId}
                                  </Text>
                                </Flex>
                                <Divider
                                  type="vertical"
                                  style={{
                                    backgroundColor: "rgba(102, 102, 102, 1)",
                                    height: 16,
                                  }}
                                />
                                <Text strong style={{ color: "white" }}>
                                  {!showOriginalPanel
                                    ? t("orders.shipment.totalCancellation")
                                    : t("orders.shipment.partialCancellation")}
                                </Text>
                              </Flex>
                              <Flex gap={8}>
                                <CloseCircleTwoTone twoToneColor="red" />
                                {shipment?.status === "pending" && (
                                  <Dropdown
                                    trigger={["click"]}
                                    menu={{
                                      items: [
                                        {
                                          key: "1",
                                          label: t("buttom.Edit.delivery"),
                                          disabled:
                                            statusDelivery.indexOf(
                                              shipment.status,
                                            ) >= 2,
                                          onClick: (e) => {
                                            e.domEvent.stopPropagation();
                                            setShowCancelPreview(false);
                                            setCancellationRequested(false);
                                          },
                                        },
                                        {
                                          key: "2",
                                          label: (
                                            <Text style={{ color: "red" }}>
                                              {isSelectMode
                                                ? t(
                                                  "buttom.Return.request.cancellation",
                                                )
                                                : t("buttom.cancel.products")}
                                            </Text>
                                          ),
                                          disabled:
                                            statusDelivery.indexOf(
                                              shipment.status,
                                            ) >= 2,
                                          onClick: (e) => {
                                            e.domEvent.stopPropagation();
                                            if (isSelectMode) {
                                              resetCancellationState();
                                            } else {
                                              handleEnterSelectMode();
                                            }
                                          },
                                        },
                                      ],
                                      onClick: (e) =>
                                        e.domEvent.stopPropagation(),
                                    }}
                                  >
                                    <Button
                                      type="link"
                                      icon={<MoreOutlined />}
                                      onClick={(e) => e.stopPropagation()}
                                      style={{ color: "white" }}
                                    />
                                  </Dropdown>
                                )}
                              </Flex>
                            </Flex>
                          ),
                          children: (
                            <Flex vertical gap={16}>
                              <Card>
                                {/* Items seleccionados */}
                                {cancelItems.map((it: any, index: number) => {
                                  const product = it.product;
                                  const linked = product?.linkedProduct;
                                  const imgSrc =
                                    linked?.variations?.[0]?.mainCatalog
                                      ?.image ||
                                    product?.variations?.[0]?.mainCatalog
                                      ?.image ||
                                    noImage;

                                  const displayName =
                                    linked?.name || product?.name || "-";
                                  const displayCatalogName =
                                    linked?.variations?.[0]?.mainCatalog
                                      ?.name ||
                                    product?.variations?.[0]?.mainCatalog
                                      ?.name ||
                                    "-";

                                  const unitIncl =
                                    computeFinalUnitInclFromDescription(
                                      product,
                                    );
                                  const rowIncl =
                                    unitIncl * Number(it.qty ?? 0);

                                  return (
                                    <>
                                      {shippingIncludedForCredit && (
                                        <>
                                          <Flex
                                            key={`cancel-${shipment.shipmentId}`}
                                            align="center"
                                            justify="space-between"
                                            style={{
                                              borderBottom:
                                                index !== cancelItems.length - 1
                                                  ? "1px solid #F0F0F0"
                                                  : "",
                                              paddingBottom:
                                                index !== cancelItems.length - 1
                                                  ? 16
                                                  : 0,
                                              marginBottom:
                                                index !== cancelItems.length - 1
                                                  ? 16
                                                  : 0,
                                            }}
                                          >
                                            {/* Imagen */}
                                            <Flex gap={12} align="center">
                                              <img
                                                src={envioImage}
                                                style={{
                                                  width: 90,
                                                  objectFit: "cover",
                                                }}
                                                alt={"Shipping"}
                                              />
                                            </Flex>

                                            {/* Descripción */}
                                            <Flex
                                              vertical
                                              style={{ width: 450 }}
                                            >
                                              <Text
                                                strong
                                                style={{ marginBottom: 16 }}
                                              >
                                                {shippingName}
                                              </Text>
                                            </Flex>

                                            <Flex>
                                              {formatPrice(
                                                N(shipment?.priceInclTax),
                                              )}
                                            </Flex>

                                            {/* Cantidad */}
                                            <Flex
                                              vertical
                                              align="end"
                                              style={{ width: 120 }}
                                            >
                                              {/* <Text>{it.qty}</Text> */}
                                              <Text>1</Text>
                                            </Flex>

                                            {/* Total del envio */}
                                            <Flex
                                              vertical
                                              align="end"
                                              style={{ minWidth: 100 }}
                                            >
                                              <Title
                                                style={{ margin: 0 }}
                                                level={5}
                                              >
                                                {formatPrice(
                                                  N(shipment?.priceInclTax),
                                                )}
                                              </Title>
                                            </Flex>
                                          </Flex>
                                          <Divider />
                                        </>
                                      )}

                                      <Flex
                                        key={`cancel-${it.orderItemId}`}
                                        align="center"
                                        justify="space-between"
                                        style={{
                                          borderBottom:
                                            index !== cancelItems.length - 1
                                              ? "1px solid #F0F0F0"
                                              : "",
                                          paddingBottom:
                                            index !== cancelItems.length - 1
                                              ? 16
                                              : 0,
                                          marginBottom:
                                            index !== cancelItems.length - 1
                                              ? 16
                                              : 0,
                                        }}
                                      >
                                        {/* Imagen */}
                                        <Flex gap={12} align="center">
                                          <img
                                            src={imgSrc}
                                            style={{
                                              width: 90,
                                              objectFit: "cover",
                                            }}
                                            alt={displayName}
                                          />
                                        </Flex>

                                        {/* Descripción */}
                                        <Flex vertical style={{ width: 450 }}>
                                          <Text
                                            strong
                                            style={{ marginBottom: 16 }}
                                          >
                                            {displayName}
                                          </Text>
                                          <Text italic>
                                            {displayCatalogName}
                                          </Text>
                                        </Flex>

                                        <Flex>
                                          <Text strong>
                                            {formatPrice(rowIncl)}
                                          </Text>
                                        </Flex>

                                        {/* Cantidad */}
                                        <Flex
                                          vertical
                                          align="end"
                                          style={{ width: 120 }}
                                        >
                                          <Text>{it.qty}</Text>
                                        </Flex>

                                        <Flex vertical style={{ minWidth: 83 }}>
                                          <Title
                                            style={{ margin: 0 }}
                                            level={5}
                                          >
                                            {formatPrice(rowIncl)}
                                          </Title>
                                          <Text type="secondary">
                                            {formatPrice(unitIncl)} x {it.qty}
                                          </Text>
                                        </Flex>
                                      </Flex>
                                    </>
                                  );
                                })}

                                {/* TOTAL */}
                                <Flex
                                  style={{
                                    marginTop: 8,
                                    backgroundColor: "#DAE8E0",
                                    padding: "8px 16px",
                                    borderRadius: "8px",
                                  }}
                                  justify="end"
                                  align="baseline"
                                  gap={60}
                                >
                                  <Title level={5} style={{ margin: 0 }}>
                                    Total
                                  </Title>
                                  <Title level={5} style={{ margin: 0 }}>
                                    {formatPrice(cancelTotalInclTax)}
                                  </Title>
                                </Flex>
                              </Card>
                            </Flex>
                          ),
                        },
                      ]}
                    />
                  )}

                {/* DATOS PRINCIPALES */}

                {shipment?.status === "canceled" ||
                  (!showCancelPreview && (
                    <Card>
                      {/* Arriba de todo: al editar, la fecha ocupa más alto que el resto y
                          los títulos tienen que seguir en la misma línea (Diseño, 2026-10-19). */}
                      <Flex
                        align="start"
                        justify="space-between"
                        gap={24}
                        className="fila-datos-entrega"
                      >
                        <Flex vertical gap={8}>
                          <Text strong type="secondary">
                            {t("orders.label.loadtype")}
                          </Text>
                          <Text>
                            {shipment?.metadata?.shippingTypeLabel || "-"}
                          </Text>
                        </Flex>
                        <Flex vertical gap={8}>
                          <Text strong type="secondary">
                            {t("orders.label.volume")}
                          </Text>
                          <Text>
                            {zippinData?.totalVolume != null
                              ? `${zippinData.totalVolume} cm³`
                              : "-"}
                          </Text>
                        </Flex>
                        <Flex vertical gap={8}>
                          <Text strong type="secondary">
                            {t("orders.label.weight")}
                          </Text>
                          <Text>
                            {zippinData?.totalWeight != null
                              ? `${(zippinData.totalWeight / 1000).toLocaleString("es-AR", { maximumFractionDigits: 2 })} kg`
                              : "-"}
                          </Text>
                        </Flex>
                        <Flex vertical gap={8}>
                          <Text strong type="secondary">
                            {t("products.import.table.date")}
                          </Text>
                          {isMoveMode ? (
                            <Flex vertical gap={6} style={{ minWidth: 190 }}>
                              <DatePicker
                                size="small"
                                format="DD/MM/YYYY"
                                value={ddDraft.day ? dayjs(ddDraft.day) : null}
                                onChange={(d) => {
                                  const next = {
                                    ...ddDraft,
                                    day: d ? d.format("YYYY-MM-DD") : undefined,
                                  };
                                  setDdDraft(next);
                                  saveDeliveryDate(next);
                                }}
                              />
                              <Flex gap={6} align="center">
                                <Input
                                  type="time"
                                  size="small"
                                  style={{ width: 92 }}
                                  value={ddDraft.from ?? ""}
                                  onChange={(e) =>
                                    setDdDraft((p) => ({
                                      ...p,
                                      from: e.target.value,
                                    }))
                                  }
                                  onBlur={() => saveDeliveryDate(ddDraft)}
                                />
                                <span>–</span>
                                <Input
                                  type="time"
                                  size="small"
                                  style={{ width: 92 }}
                                  value={ddDraft.to ?? ""}
                                  onChange={(e) =>
                                    setDdDraft((p) => ({
                                      ...p,
                                      to: e.target.value,
                                    }))
                                  }
                                  onBlur={() => saveDeliveryDate(ddDraft)}
                                />
                              </Flex>
                            </Flex>
                          ) : (
                            <Text>
                              {shipment?.metadata?.deliveryDate?.day
                                ? `${dayjs(
                                    shipment.metadata.deliveryDate.day,
                                  ).format("DD/MM/YYYY")}${
                                    shipment.metadata.deliveryDate.from &&
                                    shipment.metadata.deliveryDate.to
                                      ? ` · ${shipment.metadata.deliveryDate.from}–${shipment.metadata.deliveryDate.to}`
                                      : ""
                                  }`
                                : zippinData?.estimatedDelivery
                                  ? dayjs(zippinData.estimatedDelivery).format("DD/MM/YYYY")
                                  : "-"}
                            </Text>
                          )}
                        </Flex>
                        {/* Empresa toma el lugar que sobra y, si no entra, se corta con "…": el
                            nombre completo, al pasar el mouse. Sin el HTML que trae del método
                            de envío de Magento. */}
                        <Flex
                          vertical
                          gap={8}
                          className="fila-datos-entrega__empresa"
                        >
                          <Text strong type="secondary">
                            {t("order.label.company")}
                          </Text>
                          {(() => {
                            const empresa = nombreDeEntrega(
                              shipment?.deliveryMethod,
                            ).toUpperCase();
                            return (
                              <Text ellipsis={{ tooltip: empresa }}>
                                {empresa || "-"}
                              </Text>
                            );
                          })()}
                        </Flex>
                        <Flex vertical gap={8}>
                          <Text strong type="secondary">
                            {t("orders.label.tracking")} #
                          </Text>
                          <Text>
                            {(shipment as any)?.metadata?.trackingNumber ?? "-"}
                          </Text>
                        </Flex>
                        <Flex vertical gap={8}>
                          <Text strong type="secondary">
                            {t("orders.label.shippingLabel")}
                          </Text>
                          {zippinData?.shipmentId ? (
                            <Button
                              size="small"
                              icon={<DownloadOutlined />}
                              loading={zippinLabelDownloading}
                              onClick={handleDownloadZippinLabel}
                            >
                              {t("orders.label.shippingLabel")}
                            </Button>
                          ) : shipment?.magentoShipmentId && storeLink ? (
                            <a
                              href={`${storeLink}marketplace_zippin/shipment/downloadShippingLabel?shipment_id=${shipment.magentoShipmentId}`}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <Button
                                size="small"
                                icon={<DownloadOutlined />}
                              >
                                {t("orders.label.shippingLabel")}
                              </Button>
                            </a>
                          ) : (
                            <Text>-</Text>
                          )}
                        </Flex>
                      </Flex>

                      {/* Preguntas / extra fields del envío (label/value) que el
                          cliente completó al elegir el método de envío. */}
                      {Array.isArray(
                        shipment?.metadata?.shippingExtraInfo?.options,
                      ) &&
                        shipment.metadata.shippingExtraInfo.options.length >
                          0 && (
                          <>
                            <Divider style={{ margin: "16px 0" }} />
                            <Flex vertical gap={12}>
                              <Flex wrap gap={40}>
                                {shipment.metadata.shippingExtraInfo.options.map(
                                  (opt: any, i: number) => (
                                    <Flex
                                      vertical
                                      gap={4}
                                      key={i}
                                      style={{ minWidth: 200 }}
                                    >
                                      <Text strong type="secondary">
                                        {opt?.label}
                                      </Text>
                                      {isMoveMode ? (
                                        <Input
                                          size="small"
                                          value={questionDrafts[i]?.value ?? ""}
                                          onChange={(e) =>
                                            setQuestionDrafts((prev) => {
                                              const next = [...prev];
                                              next[i] = {
                                                label: opt?.label,
                                                value: e.target.value,
                                              };
                                              return next;
                                            })
                                          }
                                          onBlur={() =>
                                            saveQuestions(questionDrafts)
                                          }
                                        />
                                      ) : (
                                        <Text>{opt?.value || "-"}</Text>
                                      )}
                                    </Flex>
                                  ),
                                )}
                              </Flex>
                            </Flex>
                          </>
                        )}
                    </Card>
                  ))}

                {/* INSTRUCCIONES */}
                {shipment?.status === "canceled" ||
                  (showCancelPreview && (
                    <Card>
                      <Flex align="end" justify="space-between">
                        <Flex vertical gap={8}>
                          <Flex>
                            <Title level={5}>
                              {t("contacts.label.deliveryinstructions")}
                            </Title>
                          </Flex>
                          <Text strong type="secondary">
                            {t("orders.label.question")} 1
                          </Text>
                          <Text>{"-"}</Text>
                        </Flex>
                        <Flex vertical gap={8}>
                          <Text strong type="secondary">
                            {t("orders.label.question")} 2
                          </Text>
                          <Text>{"-"}</Text>
                        </Flex>
                        <Flex vertical gap={8}>
                          <Text strong type="secondary">
                            {t("orders.label.question")} 3
                          </Text>
                          <Text>{"-"}</Text>
                        </Flex>
                        <Flex vertical gap={8}>
                          <Text strong type="secondary">
                            {t("orders.label.question")} 4
                          </Text>
                          <Text>{"-"}</Text>
                        </Flex>
                      </Flex>
                    </Card>
                  ))}

                {showOriginalPanel && (
                  <>
                    {shipment?.metadata?.deliveryInstructions && (
                      <Card
                        title={
                          <Flex justify="space-between" align="center">
                            <Text strong>
                              {t("contacts.label.deliveryinstructions")}
                            </Text>
                          </Flex>
                        }
                      >
                        <Flex>
                          <Text>
                            {shipment?.metadata?.deliveryInstructions || "-"}
                          </Text>
                        </Flex>
                      </Card>
                    )}


                    {shipment?.status !== "pending" &&
                      shipment?.status !== "canceled" &&
                      shipment?.status !== "pending_cancel" &&
                      // Ocultar el remito mientras se mueven productos entre
                      // entregas: reaparece recién al confirmar el movimiento.
                      shipment?.status !== "pending_move" &&
                      !isMoveMode && (
                        <Flex className="packin-proof-card">
                          {/* Packing Slip */}
                          <Flex flex={1}>
                            <Form form={form} layout="vertical" style={{ width: "100%" }}>
                              <Flex vertical align="start" justify="start">
                                <Flex align="start" justify="start" gap={6}>
                                  <Flex gap={6}>
                                    <Text strong>
                                      {t("orders.label.packingslip")}
                                    </Text>
                                    {invoiceFileList.length === 0 && (
                                      <Text italic className="color-pending">
                                        {t("orders-label.pending")}
                                      </Text>
                                    )}
                                  </Flex>
                                  <Form.Item
                                    name="packing_slip_number"
                                  >
                                    <Input
                                      style={{ width: 136 }}
                                      defaultValue={
                                        shipment?.packingSlip?.pointOfSale || null
                                      }
                                      placeholder="-"
                                      type="number"
                                      onBlur={savePackingSlipNumbers}
                                    />
                                  </Form.Item>

                                  <Form.Item name="packing_slip">
                                    <Input
                                      style={{ width: 136 }}
                                      defaultValue={
                                        shipment?.packingSlip?.number || null
                                      }
                                      placeholder="-"
                                      type="number"
                                      onBlur={savePackingSlipNumbers}
                                    />
                                  </Form.Item>
                                </Flex>

                                <Form.Item
                                  rules={[{ required: true }]}
                                >
                                  <Upload
                                    id="packing-upload"
                                    listType="picture-card"
                                    accept="image/*,.pdf"
                                    beforeUpload={handleBeforeUpload}
                                    onPreview={handlePreview}
                                    fileList={invoiceFileList}
                                    onRemove={
                                      shipment?.packingSlip?.packingSlipId
                                        ? handleRemovePackingSlip
                                        : handleRemoveInvoiceFile
                                    }
                                    // Antes acá había `disabled` cuando el remito ya
                                    // tenía números. En antd eso apaga el componente
                                    // entero, así que también mataba el ícono de borrar:
                                    // el PDF quedaba congelado para siempre
                                    // (xcons-docs#346, el mismo caso que la NC).
                                    //
                                    // Ahora el botón de subir se esconde no renderizando el
                                    // children —con un remito ya guardado no hay nada que
                                    // agregar— y la papelera queda viva. Al borrar, el
                                    // `softDelete` sobre el `OneToOne` devuelve la relación a
                                    // `null` y el botón vuelve solo.
                                    //
                                    // La papelera sí se esconde una vez publicado en Magento:
                                    // ahí el `ship_name` ya lleva el número.
                                    showUploadList={{
                                      showRemoveIcon: !shipment?.magentoShipmentId,
                                    }}
                                    customRequest={({ file, onSuccess }: any) => {
                                      setInvoiceFileList((prev: any) => [
                                        ...prev,
                                        {
                                          uid: file.uid,
                                          name: file.name,
                                          status: "done",
                                          url: URL.createObjectURL(file),
                                          originFileObj: file,
                                          type: file.type || (file.name?.toLowerCase().endsWith(".pdf") ? "application/pdf" : "image/jpeg"),
                                        },
                                      ]);
                                      if (onSuccess) onSuccess("ok");
                                    }}
                                  >
                                    {!shipment?.packingSlip?.packingSlipId &&
                                      `+ ${t("buttons.upload")}`}
                                  </Upload>
                                </Form.Item>
                                <Flex
                                  gap={8}
                                  justify="end"
                                  style={{ marginLeft: "auto" }}
                                >
                                  {/* Una vez enviado a Magento solo queda el
                                      botón de reenviar; antes, solo el de
                                      enviar. No conviven. */}
                                  {Boolean(shipment?.magentoShipmentId) ? (
                                    <Button
                                      size="small"
                                      loading={loadingStep}
                                      onClick={handleResendPackingSlip}
                                    >
                                      {t("orders.packingSlip.resend")}
                                    </Button>
                                  ) : (
                                    // Secundario, como el de Reenviar (Diseño, 2026-10-19).
                                    <Button
                                      onClick={handlePackingUpload}
                                      size="small"
                                      disabled={
                                        !!(shipment?.packingSlip?.pointOfSale ||
                                          shipment?.packingSlip?.number) ||
                                        invoiceFileList.length === 0
                                      }
                                    >
                                      Enviar remito
                                    </Button>
                                  )}
                                </Flex>
                              </Flex>
                            </Form>
                          </Flex>

                          <Divider type="vertical" className="packin-proof-card__linea" />

                          {/* Proof of Delivery: siempre a la derecha del remito, deshabilitado
                              hasta que la entrega está Entregada (Diseño, 2026-10-19). Antes
                              aparecía recién en tránsito. */}
                          {(() => {
                            const podHabilitado =
                              shipment?.status === "delivered";
                            return (
                              <Flex flex={1}>
                                <Form style={{ width: "100%" }} form={form} layout="vertical">
                                  <Flex style={{ minHeight: 158 }} vertical align="start" justify="start">
                                    <Flex gap={6} style={{ height: 56 }}>
                                      <Text strong>{t("proof.delivery")}</Text>
                                      {fileList.length === 0 && (
                                        <Text italic className="color-pending">
                                          {t("orders-label.pending")}
                                        </Text>
                                      )}
                                    </Flex>
                                    <Flex align="start">
                                      <Form.Item
                                        rules={[{ required: true }]}
                                      >
                                        <Upload
                                          id="proof-upload"
                                          disabled={!podHabilitado}
                                          listType="picture-card"
                                          accept="image/*,.pdf"
                                          beforeUpload={handleBeforeUpload}
                                          onPreview={handlePreview}
                                          fileList={fileList}
                                          onRemove={(file: any) => {
                                            const isRemotePod = (
                                              shipment?.proofsOfDelivery ?? []
                                            ).some(
                                              (pod: any) =>
                                                pod.proofOfDeliveryId?.toString() ===
                                                file.uid,
                                            );
                                            return isRemotePod
                                              ? handleRemoveProofOfDelivery(file)
                                              : handleRemoveFile(file);
                                          }}
                                          showUploadList={{ showRemoveIcon: true }}
                                          customRequest={({ file, onSuccess }: any) => {
                                            setFileList((prev: any) => [
                                              ...prev,
                                              {
                                                uid: file.uid,
                                                name: file.name,
                                                status: "done",
                                                url: URL.createObjectURL(file),
                                                originFileObj: file,
                                                type: file.type || (file.name?.toLowerCase().endsWith(".pdf") ? "application/pdf" : "image/jpeg"),
                                              },
                                            ]);
                                            if (onSuccess) onSuccess("ok");
                                          }}
                                        >
                                          + {t("buttons.upload")}
                                        </Upload>
                                      </Form.Item>
                                    </Flex>
                                    {/* Una vez que hay un comprobante subido,
                                        el botón pasa a "Reenviar" (secundario);
                                        antes, "Enviar" (primario). Ambos suben
                                        a S3 — el comprobante no va a Magento. */}
                                    {(shipment?.proofsOfDelivery?.length ?? 0) > 0 ? (
                                      <Button
                                        style={{ marginLeft: "auto" }}
                                        onClick={handleProofUpload}
                                        disabled={!podHabilitado || fileList.length === 0}
                                        size="small"
                                      >
                                        Reenviar comprobante de entrega
                                      </Button>
                                    ) : (
                                      // Secundario, como el de Reenviar (Diseño, 2026-10-19).
                                      <Button
                                        style={{ marginLeft: "auto" }}
                                        onClick={handleProofUpload}
                                        disabled={!podHabilitado || fileList.length === 0}
                                        size="small"
                                      >
                                        Enviar comprobante de entrega
                                      </Button>
                                    )}
                                  </Flex>
                                </Form>
                              </Flex>
                            );
                          })()}
                        </Flex>
                      )}

                    <Card
                      // 8px de relleno; cancelada, sin relleno arriba y abajo, así el fondo
                      // rojo de los productos llega a los bordes (Diseño, 2026-10-19).
                      className={
                        shipment?.status === "canceled"
                          ? "tarjeta-productos tarjeta-productos-cancelada"
                          : "tarjeta-productos"
                      }
                      style={
                        (isMoveMode && moveTarget) ||
                        (isSelectMode && showCancelPreview) ||
                        shipment?.status === "pending_move"
                          ? { display: "none" }
                          : undefined
                      }
                    >
                      {/* "Seleccionar todos" — visible solo durante el cancel
                          individual (isSelectMode), antes de la lista. */}
                      {isSelectMode && !showCancelPreview && (
                        <Flex
                          align="center"
                          gap={8}
                          style={{
                            padding: "4px 0 16px 0",
                            borderBottom: "1px solid #F0F0F0",
                            marginBottom: 16,
                          }}
                        >
                          <Checkbox
                            checked={allSelected}
                            disabled={cancellationRequested}
                            indeterminate={
                              selectedItems.length > 0 && !allSelected
                            }
                            onChange={(e) =>
                              handleSelectAll(e.target.checked)
                            }
                          />
                          <Text>
                            {t("orders.selectAll", {
                              defaultValue: "Seleccionar todos",
                            })}
                          </Text>
                        </Flex>
                      )}
                      {isMoveMode &&
                        (() => {
                          const totalQty = (shipment?.items ?? []).reduce(
                            (acc: number, it: any) =>
                              acc + (Number(it?.qty) || 0),
                            0,
                          );
                          const movingQty = Object.values(moveQtyByItemId).reduce(
                            (acc: number, q: any) => acc + (Number(q) || 0),
                            0,
                          );
                          const allSelected =
                            totalQty > 0 && movingQty >= totalQty;
                          const someSelected = movingQty > 0 && !allSelected;
                          return (
                            <Flex
                              align="center"
                              gap={8}
                              style={{
                                padding: "8px 12px",
                                marginBottom: 8,
                                background: "#fafafa",
                                borderRadius: 4,
                              }}
                            >
                              <Checkbox
                                checked={allSelected}
                                indeterminate={someSelected}
                                onChange={(e) =>
                                  handleMoveSelectAll(e.target.checked)
                                }
                              />
                              <Text type="secondary">
                                {t("orders.shipment.selectAll")}
                              </Text>
                            </Flex>
                          );
                        })()}
                      {showShippingLine && (
                        <Flex
                          key={`shipment-${shipment.shipmentId}`}
                          align="center"
                          justify="space-between"
                          className="fila-producto"
                          style={{
                            borderBottom: itemsForOriginalPanel.length
                              ? "1px solid #F0F0F0"
                              : undefined,
                            // Envío cancelado: mismo tratamiento visual que un
                            // item cancelado normal (fondo colorErrorBg), en vez
                            // de tachado + label.
                            ...(isShippingCanceled
                              ? {
                                  backgroundColor:
                                    "#fff2f0" /* antd colorErrorBg */,
                                  padding: 8,
                                  borderRadius: 4,
                                }
                              : {}),
                          }}
                        >
                          {/* Icono/imagen del envío + checkbox cancel */}
                          {/* Imagen y descripción juntas, a 16px (Diseño, 2026-10-19). */}
                          <Flex gap={16} align="center" style={{ flex: 1, minWidth: 0 }}>
                          <Flex gap={12} align="center">
                            {isSelectMode && !showCancelPreview && (
                              <Checkbox
                                checked={cancelShipping}
                                disabled={cancellationRequested}
                                onChange={(e) =>
                                  setCancelShipping(e.target.checked)
                                }
                              />
                            )}
                            <img
                              src={envioImage}
                              style={{
                                width: 90,
                                objectFit: "cover",
                              }}
                              alt={"Shipping"}
                            />
                          </Flex>

                          {/* Descripción */}
                          <Flex vertical style={{ flex: 1, minWidth: 0 }}>
                            <Text strong>{shippingName}</Text>
                          </Flex>
                          </Flex>

                          <Flex style={{ minWidth: 83 }} align="center">
                            <DiscountPrice shipment={shipment} />
                          </Flex>

                          {/* Qty/stock — en isSelectMode con shipping marcado
                              muestra "Cancel 1" rojo. En el resto, "1". */}
                          <Flex vertical align="end" style={{ width: 120 }}>
                            {isSelectMode && !showCancelPreview && cancelShipping ? (
                              <Flex align="center" gap={6}>
                                <Text style={{ color: "#ff4d4f" }}>
                                  {t("orders.shipment.cancel")}
                                </Text>
                                <Text strong>1</Text>
                              </Flex>
                            ) : (
                              <Text>1</Text>
                            )}
                          </Flex>

                          {/* Total de la línea (con IVA). En modo select
                              sin preview con shipping marcado, el subtotal
                              cae a 0 (igual que los items con qty=0). */}
                          <Flex
                            align="end"
                            style={{ minWidth: 100 }}
                          >
                            <Title
                              style={{
                                margin: 0,
                                textAlign: "right",
                                width: "100%",
                              }}
                              level={5}
                            >
                              {isSelectMode &&
                              !showCancelPreview &&
                              cancelShipping
                                ? formatPrice(0)
                                : shippingPriceInclTax === 0
                                  ? t("orders.shipment.free")
                                  : formatPrice(shippingPriceInclTax)}
                            </Title>
                          </Flex>
                        </Flex>
                      )}
                      {itemsForOriginalPanel.map((item: any, index: number) => {
                        const product = item.product;
                        const linked = product?.linkedProduct;

                        const imgSrc =
                          linked?.variations?.[0]?.mainCatalog?.image ||
                          product?.variations?.[0]?.mainCatalog?.image ||
                          noImage;

                        const displayName =
                          linked?.name || product?.name || "-";
                        const displayCatalogName =
                          linked?.variations?.[0]?.mainCatalog?.name ||
                          product?.variations?.[0]?.mainCatalog?.name ||
                          "-";
                        const displaySku = linked?.sku || product?.sku || "N/A";

                        const qtyOriginal = Number(item.qty) || 0;
                        const qtyRemanente = Number(item.displayQty) || 0;

                        const unitFinalIncl = getUnitFinalInclFromDescription(
                          product ?? item.product ?? {},
                        );
                        const lineTotalIncl = cents(
                          unitFinalIncl * qtyRemanente,
                        );

                        // Look up optimization services for this product
                        const productId = product?.productId;
                        const services = productId != null
                          ? optimizationServiceMap[String(productId)]
                          : undefined;
                        const corteItem = services?.corteItem ?? null;
                        const corteQty = services?.corteQty ?? 0;
                        const cantoItem = services?.cantoItem ?? null;
                        const cantoQty = services?.cantoQty ?? 0;
                        const cantoFisicoItem = services?.cantoFisicoItem ?? null;
                        const cantoFisicoQty = services?.cantoFisicoQty ?? 0;
                        const downloadUrls = services?.downloadUrls ?? {};

                        const moveRow = isMoveMode
                          ? moveItemRowState(item)
                          : null;

                        return (
                          <Flex
                            key={item.orderItemId}
                            vertical
                            className="fila-producto"
                            style={{
                              borderBottom:
                                index !== itemsForOriginalPanel.length - 1
                                  ? "1px solid #F0F0F0"
                                  : "",
                              // Background:
                              //  - gold-1 cuando hay items marcados para
                              //    mover (move mode, etapa 2 move ticket).
                              //  - colorErrorBg cuando hay items marcados
                              //    para cancelar (cancel mode, etapa 2 cancel
                              //    ticket) O cuando el shipment ya está
                              //    cancelado (módulo cancelado read-only).
                              backgroundColor:
                                moveRow && moveRow.move > 0
                                  ? "#fffbe6" /* antd gold-1 */
                                  : shipment?.status === "canceled"
                                    ? "#fff2f0" /* antd colorErrorBg */
                                    : isSelectMode &&
                                        !showCancelPreview &&
                                        Number(
                                          editableQuantities[
                                            item.orderItemId
                                          ] || 0,
                                        ) > 0
                                      ? "#fff2f0" /* antd colorErrorBg */
                                      : undefined,
                              padding:
                                (moveRow && moveRow.move > 0) ||
                                shipment?.status === "canceled" ||
                                (isSelectMode &&
                                  !showCancelPreview &&
                                  Number(
                                    editableQuantities[item.orderItemId] || 0,
                                  ) > 0)
                                  ? 8
                                  : undefined,
                              borderRadius:
                                (moveRow && moveRow.move > 0) ||
                                shipment?.status === "canceled" ||
                                (isSelectMode &&
                                  !showCancelPreview &&
                                  Number(
                                    editableQuantities[item.orderItemId] || 0,
                                  ) > 0)
                                  ? 4
                                  : undefined,
                            }}
                          >
                            {/* Main Product Row */}
                            <Flex align="center" justify="space-between">
                              {/* Imagen y nombre juntos, a 16px (Diseño, 2026-10-19). */}
                              <Flex gap={16} align="center" style={{ flex: 1, minWidth: 0 }}>
                              <Flex gap={12} align="center">
                                {isSelectMode && !showCancelPreview && (() => {
                                  const total = Number(item?.qty) || 0;
                                  const cancelQty = Number(
                                    editableQuantities[item.orderItemId] || 0,
                                  );
                                  const checked = cancelQty > 0 && cancelQty >= total;
                                  const indeterminate =
                                    cancelQty > 0 && cancelQty < total;
                                  return (
                                    <Checkbox
                                      checked={checked}
                                      indeterminate={indeterminate}
                                      onChange={(e) => {
                                        e.stopPropagation();
                                        // Toggle binario sobre el item: marcar
                                        // todo o desmarcar. Para qty parcial el
                                        // operador usa el InputNumber Cancel.
                                        if (e.target.checked) {
                                          if (
                                            !selectedItems.some(
                                              (s) =>
                                                s.orderItemId ===
                                                item.orderItemId,
                                            )
                                          ) {
                                            handleItemCheck(item, true);
                                          }
                                          setEditableQuantities((prev) => ({
                                            ...prev,
                                            [item.orderItemId]: total,
                                          }));
                                        } else {
                                          handleItemCheck(item, false);
                                        }
                                      }}
                                      onClick={(e) => e.stopPropagation()}
                                    />
                                  );
                                })()}
                                {isMoveMode && moveRow && (
                                  <Checkbox
                                    checked={moveRow.checked}
                                    indeterminate={moveRow.indeterminate}
                                    onChange={(e) => {
                                      e.stopPropagation();
                                      handleMoveQtyChange(
                                        item.orderItemId,
                                        e.target.checked ? moveRow.total : 0,
                                        moveRow.total,
                                      );
                                    }}
                                    onClick={(e) => e.stopPropagation()}
                                  />
                                )}
                                <img
                                  src={imgSrc || noImage}
                                  style={{ width: 90, objectFit: "cover" }}
                                  alt={displayName}
                                />
                              </Flex>

                              <Flex vertical style={{ flex: 1, minWidth: 0 }}>
                                <Flex vertical>
                                  <Text strong style={{ marginBottom: Object.keys(downloadUrls).length > 0 ? 6 : 16 }}>
                                    {displayName}
                                  </Text>
                                  {Object.keys(downloadUrls).length > 0 && (
                                    <Dropdown
                                      trigger={["click"]}
                                      menu={{
                                        items: Object.entries(downloadUrls).map(([ext, urls]) => ({
                                          key: ext,
                                          label: ext.toUpperCase(),
                                          onClick: () => handleDownloadGroup(urls),
                                        })),
                                      }}
                                    >
                                      <Button
                                        type="default"
                                        icon={<DownOutlined />}
                                        size="small"
                                        iconPosition="end"
                                        style={{ alignSelf: "flex-start", marginBottom: 16 }}
                                      >
                                        Optimización
                                      </Button>
                                    </Dropdown>
                                  )}
                                </Flex>
                                <Text italic>{displayCatalogName}</Text>
                                <Text>Seller SKU {displaySku}</Text>
                              </Flex>
                              </Flex>

                              <Flex vertical justify="center" align="end" style={{ minWidth: 120 }}>
                                <DiscountPrice item={product} />
                              </Flex>

                              <Flex vertical align="end" style={{ width: 120 }}>
                                {/* qty total — oculto en modo move (editar
                                    entregas): solo se muestran Keep/Move */}
                                {!isMoveMode &&
                                  !(isSelectMode && !showCancelPreview) && (
                                    <Text>{qtyRemanente}</Text>
                                  )}
                                {/* Stock info — oculta en cancel mode
                                    (spec XCONS-1929 etapa 2) y en move mode */}
                                {linked?.stock !== undefined &&
                                  !(isSelectMode && !showCancelPreview) &&
                                  !isMoveMode && (
                                  <Text
                                    style={{
                                      textAlign: "right",
                                      color:
                                        linked?.stock > qtyRemanente
                                          ? "rgba(70, 140, 105, 1)"
                                          : "rgba(255, 197, 61, 1)",
                                    }}
                                  >
                                    {linked?.stock > qtyRemanente
                                      ? t("orders.stockAvailable")
                                      : `${linked?.stock} disponibles\n+ Stock en ${linked?.leadTime} días`}
                                  </Text>
                                )}
                                {isSelectMode && !showCancelPreview && (() => {
                                  const total = Number(qtyOriginal) || 0;
                                  const cancelQty = Number(
                                    editableQuantities[item.orderItemId] || 0,
                                  );
                                  const keep = Math.max(0, total - cancelQty);
                                  return (
                                    <Flex
                                      vertical
                                      align="end"
                                      gap={4}
                                      style={{ marginTop: 8 }}
                                    >
                                      <Flex align="center" gap={6}>
                                        <Text
                                          type="secondary"
                                          style={{ fontSize: 12 }}
                                        >
                                          {t("orders.shipment.keep")}
                                        </Text>
                                        <Text strong>{keep}</Text>
                                      </Flex>
                                      <Flex align="center" gap={6}>
                                        <Text
                                          style={{
                                            color: "#ff4d4f" /* colorError */,
                                            fontSize: 12,
                                          }}
                                        >
                                          {t("orders.shipment.cancel")}
                                        </Text>
                                        <Input
                                          type="number"
                                          min={0}
                                          max={total}
                                          value={cancelQty}
                                          style={{
                                            width: 70,
                                            textAlign: "right",
                                          }}
                                          onClick={(e) => e.stopPropagation()}
                                          onChange={(e) => {
                                            const parsed = parseInt(
                                              e.target.value,
                                              10,
                                            );
                                            const newQty = Math.max(
                                              0,
                                              Math.min(
                                                Number.isNaN(parsed)
                                                  ? 0
                                                  : parsed,
                                                total,
                                              ),
                                            );
                                            // Mantener selectedItems coherente:
                                            // si pasa de 0 a >0 → marcar; si
                                            // vuelve a 0 → desmarcar.
                                            const wasSelected = selectedItems.some(
                                              (s) =>
                                                s.orderItemId ===
                                                item.orderItemId,
                                            );
                                            if (newQty > 0 && !wasSelected) {
                                              setSelectedItems((prev) => [
                                                ...prev,
                                                item,
                                              ]);
                                            } else if (
                                              newQty === 0 &&
                                              wasSelected
                                            ) {
                                              setSelectedItems((prev) =>
                                                prev.filter(
                                                  (s) =>
                                                    s.orderItemId !==
                                                    item.orderItemId,
                                                ),
                                              );
                                            }
                                            setEditableQuantities((prev) => ({
                                              ...prev,
                                              [item.orderItemId]: newQty,
                                            }));
                                          }}
                                        />
                                      </Flex>
                                    </Flex>
                                  );
                                })()}
                                {isMoveMode && moveRow && (
                                  <Flex
                                    vertical
                                    align="end"
                                    gap={4}
                                    style={{ marginTop: 8 }}
                                  >
                                    <Flex align="center" gap={6}>
                                      <Text type="secondary" style={{ fontSize: 12 }}>
                                        {t("orders.shipment.keep")}
                                      </Text>
                                      <Text strong>{moveRow.keep}</Text>
                                    </Flex>
                                    <Flex align="center" gap={6}>
                                      <Text style={{ color: "#d48806", fontSize: 12 }}>
                                        {t("orders.shipment.move")}
                                      </Text>
                                      <Input
                                        type="number"
                                        min={0}
                                        max={moveRow.total}
                                        value={moveRow.move}
                                        style={{ width: 70, textAlign: "right" }}
                                        onClick={(e) => e.stopPropagation()}
                                        onChange={(e) => {
                                          const parsed = parseInt(
                                            e.target.value,
                                            10,
                                          );
                                          handleMoveQtyChange(
                                            item.orderItemId,
                                            Number.isNaN(parsed) ? 0 : parsed,
                                            moveRow.total,
                                          );
                                        }}
                                      />
                                    </Flex>
                                  </Flex>
                                )}
                              </Flex>

                              <Flex vertical align="end" style={{ minWidth: 100 }}>
                                <Title style={{ margin: 0 }} level={5}>
                                  {formatPrice(lineTotalIncl)}
                                </Title>
                              </Flex>
                            </Flex>

                            {/* Service Rows (Corte, Canto, Canto Fisico) */}
                            {[
                              { show: !!corteItem, item: corteItem, qty: corteQty, icon: corteImage, label: "Servicio de corte" },
                              { show: !!cantoItem, item: cantoItem, qty: cantoQty, icon: cantoImage, label: "Servicio de pegado de cantos" },
                              { show: !!cantoFisicoItem, item: cantoFisicoItem, qty: cantoFisicoQty, icon: cantoFisicoItem?.linkedProduct?.variations?.[0]?.mainCatalog?.image || cantoImage, label: "Canto ABS" }
                            ].map((service, idx) => service.show && (
                              <Flex key={`${item.orderItemId}-service-${idx}`} align="center" justify="space-between" style={{ marginTop: 12 }}>
                                {/* Icon Column */}
                                <Flex align="center" justify="end" style={{ width: 102 + (isSelectMode && !showCancelPreview ? 32 : 0) }}>
                                  <Larrow style={{ marginRight: 10 }} />
                                  <img
                                    src={service.icon}
                                    style={{ width: 47, height: 47, objectFit: "cover", borderRadius: 4 }}
                                    alt={service.label}
                                  />
                                </Flex>

                                {/* Description Column */}
                                <Flex vertical style={{ flex: 1, minWidth: 0 }}>
                                  <Text strong>
                                    {service.item?.linkedProduct?.variations?.[0]?.mainCatalog?.name
                                      || service.item?.linkedProduct?.name
                                      || service.label}
                                  </Text>
                                </Flex>

                                {/* Price Column */}
                                <Flex vertical justify="center" align="end" style={{ minWidth: 120 }}>
                                  <DiscountPrice item={service.item} />
                                </Flex>

                                {/* Qty Column */}
                                <Flex vertical align="end" style={{ width: 120 }}>
                                  <Text strong>{service.qty || 1}</Text>
                                </Flex>

                                {/* Total Column */}
                                <Flex vertical align="end" style={{ minWidth: 100 }}>
                                  <Title style={{ margin: 0 }} level={5}>
                                    {formatPrice(cents(getUnitFinalInclFromDescription(service.item) * (service.qty || 1)))}
                                  </Title>
                                </Flex>
                              </Flex>
                            ))}
                          </Flex>
                        );
                      })}

                    </Card>

                    {/* MOVE SUMMARY (preview local, antes de pegar POST).
                        Render rico: imagen + nombre + SKU + precio unitario
                        con descuento + qty + subtotal. Mismo layout que el
                        cancel-preview y el preview pending_move. */}
                    {isMoveMode && moveTarget && (() => {
                      const sourceItems = shipment?.items ?? [];
                      const toMove = sourceItems
                        .map((it: any) => ({
                          item: it,
                          qty: Number(moveQtyByItemId[it.orderItemId] || 0),
                        }))
                        .filter((x: any) => x.qty > 0);
                      const toKeep = sourceItems
                        .map((it: any) => {
                          const total = Number(it.qty) || 0;
                          const move = Number(
                            moveQtyByItemId[it.orderItemId] || 0,
                          );
                          return { item: it, qty: Math.max(0, total - move) };
                        })
                        .filter((x: any) => x.qty > 0);

                      const renderRow = (
                        row: { item: any; qty: number },
                        index: number,
                        variant: "moving" | "kept",
                        listLength: number,
                      ) => {
                        const product = productsMap?.[row.item.orderItemId];
                        const linked = product?.linkedProduct;
                        const imgSrc =
                          linked?.variations?.[0]?.mainCatalog?.image ||
                          product?.variations?.[0]?.mainCatalog?.image ||
                          noImage;
                        const displayName =
                          linked?.name || product?.name || "-";
                        const displayCatalogName =
                          linked?.variations?.[0]?.mainCatalog?.name ||
                          product?.variations?.[0]?.mainCatalog?.name ||
                          "-";
                        const displaySku = product?.sellerSku || "-";
                        const unitFinalIncl = product
                          ? getUnitFinalInclFromDescription(product)
                          : 0;
                        const lineTotalIncl = cents(unitFinalIncl * row.qty);

                        return (
                          <Flex
                            key={`mv-local-${variant}-${row.item.orderItemId}`}
                            align="center"
                            justify="space-between"
                            className="fila-producto"
                            style={{
                              borderBottom:
                                index !== listLength - 1
                                  ? "1px solid #f0f0f0"
                                  : "",
                            }}
                          >
                            {/* Imagen y nombre juntos, a 16px (Diseño, 2026-10-19). */}
                            <Flex gap={16} align="center" style={{ flex: 1, minWidth: 0 }}>
                            <Flex gap={12} align="center">
                              <img
                                src={imgSrc}
                                style={{ width: 90, objectFit: "cover" }}
                                alt={displayName}
                              />
                            </Flex>
                            <Flex vertical style={{ flex: 1, minWidth: 0 }}>
                              <Text strong style={{ marginBottom: 16 }}>
                                {displayName}
                              </Text>
                              <Text italic>{displayCatalogName}</Text>
                              <Text>Seller SKU {displaySku}</Text>
                            </Flex>
                            </Flex>
                            <Flex
                              vertical
                              justify="center"
                              align="end"
                              style={{ minWidth: 120 }}
                            >
                              <DiscountPrice item={product} />
                            </Flex>
                            <Flex
                              vertical
                              align="end"
                              style={{ width: 120 }}
                            >
                              {variant === "moving" ? (
                                <Flex align="center" gap={6}>
                                  <Text style={{ color: "#d48806" }}>
                                    {t("orders.shipment.move")}
                                  </Text>
                                  <Text strong>{row.qty}</Text>
                                </Flex>
                              ) : (
                                <Text>{row.qty}</Text>
                              )}
                            </Flex>
                            <Flex
                              vertical
                              align="end"
                              style={{ minWidth: 100 }}
                            >
                              <Title style={{ margin: 0 }} level={5}>
                                {formatPrice(lineTotalIncl)}
                              </Title>
                            </Flex>
                          </Flex>
                        );
                      };

                      const computeTotal = (rows: { item: any; qty: number }[]) =>
                        cents(
                          rows.reduce((acc, r) => {
                            const product = productsMap?.[r.item.orderItemId];
                            const unit = product
                              ? getUnitFinalInclFromDescription(product)
                              : 0;
                            return acc + unit * r.qty;
                          }, 0),
                        );
                      const movingTotal = computeTotal(toMove);
                      const keptTotal = computeTotal(toKeep);

                      const TotalRow = ({ amount }: { amount: number }) => (
                        <Flex
                          style={{
                            marginTop: 8,
                            backgroundColor: "#DAE8E0",
                            padding: "8px 16px",
                            borderRadius: "8px",
                          }}
                          justify="end"
                          align="baseline"
                          gap={60}
                        >
                          <Title level={5} style={{ margin: 0 }}>
                            Total
                          </Title>
                          <Title level={5} style={{ margin: 0 }}>
                            {formatPrice(amount)}
                          </Title>
                        </Flex>
                      );

                      const targetLabelText = t(
                        "orders.shipment.productsToBeMoving",
                        {
                          target:
                            moveTarget.mode === "existing"
                              ? moveTarget.label
                              : t("orders.shipment.newDelivery"),
                        },
                      );

                      return (
                        // Todos los espacios en 8px (Diseño, 2026-10-19).
                        <Flex vertical gap={8} style={{ marginTop: 8 }}>
                          <Card
                            className="tarjeta-productos tarjeta-encabezado-32"
                            style={{ background: "#fffbe6" }}
                            title={targetLabelText}
                          >
                            {toMove.map((row: any, idx: number) =>
                              renderRow(row, idx, "moving", toMove.length),
                            )}
                            <TotalRow amount={movingTotal} />
                          </Card>
                          {toKeep.length > 0 && (
                            <Card
                              className="tarjeta-productos tarjeta-encabezado-32"
                              style={{ background: "rgba(0, 0, 0, 0.06)" }}
                              title={t("orders.shipment.productsThatAreKept")}
                            >
                              {toKeep.map((row: any, idx: number) =>
                                renderRow(row, idx, "kept", toKeep.length),
                              )}
                              <TotalRow amount={keptTotal} />
                            </Card>
                          )}
                        </Flex>
                      );
                    })()}

                    {/* MOVE CONFIRMATION MODAL — etapa 4 */}
                    <Modal
                      open={moveConfirmOpen}
                      onCancel={() => setMoveConfirmOpen(false)}
                      title={t("orders.shipment.moveConfirm.title")}
                      okText={t("orders.shipment.moveConfirm.yes")}
                      cancelText={t("orders.shipment.cancel")}
                      confirmLoading={moveLoading}
                      onOk={() => handleConfirmMove()}
                    >
                      <Text>
                        {moveTarget?.mode === "new"
                          ? t("orders.shipment.moveConfirm.contentNew")
                          : moveTarget?.mode === "existing"
                            ? t("orders.shipment.moveConfirm.contentExisting", {
                                target: moveTarget.label,
                              })
                            : ""}
                      </Text>
                    </Modal>

                    {/* CANCEL PREVIEW (2-bloques) — etapa 3 del ticket
                        XCONS-1929. Filas ricas con image + name + sku +
                        price + qty + subtotal. Mismo layout que la lista
                        normal pero con label "Cancel" rojo en el bloque
                        cancelled. Si tiene servicios de optimización, se
                        muestran como subfilas proporcionales al qty. */}
                    {isSelectMode && showCancelPreview && (() => {
                      const sourceItems = shipment?.items ?? [];
                      const toCancel = sourceItems
                        .map((it: any) => ({
                          item: it,
                          qty: Number(
                            editableQuantities[it.orderItemId] || 0,
                          ),
                        }))
                        .filter((x: any) => x.qty > 0);
                      const toKeep = sourceItems
                        .map((it: any) => {
                          const total = Number(it.qty) || 0;
                          const cancelQty = Number(
                            editableQuantities[it.orderItemId] || 0,
                          );
                          return {
                            item: it,
                            qty: Math.max(0, total - cancelQty),
                          };
                        })
                        .filter((x: any) => x.qty > 0);

                      const isTotalCancelPreview =
                        toCancel.length > 0 && toKeep.length === 0;
                      // El shipping va al bloque "cancelled" si el operador
                      // lo marcó explícitamente o si es full cancel.
                      const shippingInCancelled =
                        cancelShipping || isTotalCancelPreview;

                      // Render rico de una fila de producto + sus servicios.
                      const renderProductRow = (
                        row: { item: any; qty: number },
                        index: number,
                        variant: "cancelled" | "kept",
                        listLength: number,
                      ) => {
                        const product = productsMap?.[row.item.orderItemId];
                        const linked = product?.linkedProduct;
                        const imgSrc =
                          linked?.variations?.[0]?.mainCatalog?.image ||
                          product?.variations?.[0]?.mainCatalog?.image ||
                          noImage;
                        const displayName =
                          linked?.name || product?.name || "-";
                        const displayCatalogName =
                          linked?.variations?.[0]?.mainCatalog?.name ||
                          product?.variations?.[0]?.mainCatalog?.name ||
                          "-";
                        const displaySku = product?.sellerSku || "-";
                        const unitFinalIncl = product
                          ? getUnitFinalInclFromDescription(product)
                          : 0;
                        const lineTotalIncl = cents(unitFinalIncl * row.qty);

                        // Servicios proporcionales (mismo mapping que la
                        // lista normal). Se prorratean por la fracción de
                        // qty (cancel/total) para que cuadre la subtotal.
                        const productId = product?.productId;
                        const services =
                          productId != null
                            ? optimizationServiceMap[String(productId)]
                            : undefined;
                        const originalQty =
                          Number(row.item.qty) || row.qty || 1;
                        const ratio = originalQty > 0 ? row.qty / originalQty : 1;
                        const serviceRows = services
                          ? [
                              services.corteItem && {
                                item: services.corteItem,
                                qty: Math.max(
                                  0,
                                  Math.round(
                                    (services.corteQty || 0) * ratio,
                                  ),
                                ),
                                icon: corteImage,
                                label: "Servicio de corte",
                              },
                              services.cantoItem && {
                                item: services.cantoItem,
                                qty: Math.max(
                                  0,
                                  Math.round(
                                    (services.cantoQty || 0) * ratio,
                                  ),
                                ),
                                icon: cantoImage,
                                label:
                                  "Servicio de pegado de cantos",
                              },
                              services.cantoFisicoItem && {
                                item: services.cantoFisicoItem,
                                qty: Math.max(
                                  0,
                                  Math.round(
                                    (services.cantoFisicoQty || 0) * ratio,
                                  ),
                                ),
                                icon:
                                  services.cantoFisicoItem?.linkedProduct
                                    ?.variations?.[0]?.mainCatalog?.image ||
                                  cantoImage,
                                label: "Canto ABS",
                              },
                            ].filter((s: any) => s && s.qty > 0)
                          : [];

                        return (
                          <Flex
                            key={`${variant}-${row.item.orderItemId}`}
                            vertical
                            className="fila-producto"
                            style={{
                              borderBottom:
                                index !== listLength - 1
                                  ? "1px solid #f0f0f0"
                                  : "",
                            }}
                          >
                            {/* Fila principal */}
                            <Flex align="center" justify="space-between">
                              {/* Imagen y nombre juntos, a 16px (Diseño, 2026-10-19). */}
                              <Flex gap={16} align="center" style={{ flex: 1, minWidth: 0 }}>
                              <Flex gap={12} align="center">
                                <img
                                  src={imgSrc}
                                  style={{
                                    width: 90,
                                    objectFit: "cover",
                                  }}
                                  alt={displayName}
                                />
                              </Flex>
                              <Flex vertical style={{ flex: 1, minWidth: 0 }}>
                                <Text
                                  strong
                                  style={{ marginBottom: 16 }}
                                >
                                  {displayName}
                                </Text>
                                <Text italic>{displayCatalogName}</Text>
                                <Text>Seller SKU {displaySku}</Text>
                              </Flex>
                              </Flex>
                              <Flex
                                vertical
                                justify="center"
                                align="end"
                                style={{ minWidth: 120 }}
                              >
                                <DiscountPrice item={product} />
                              </Flex>
                              <Flex
                                vertical
                                align="end"
                                style={{ width: 120 }}
                              >
                                {variant === "cancelled" ? (
                                  <Flex align="center" gap={6}>
                                    <Text
                                      style={{
                                        color:
                                          "#ff4d4f" /* colorError */,
                                      }}
                                    >
                                      {t("orders.shipment.cancel")}
                                    </Text>
                                    <Text strong>{row.qty}</Text>
                                  </Flex>
                                ) : (
                                  <Text>{row.qty}</Text>
                                )}
                              </Flex>
                              <Flex
                                vertical
                                align="end"
                                style={{ minWidth: 100 }}
                              >
                                <Title style={{ margin: 0 }} level={5}>
                                  {formatPrice(lineTotalIncl)}
                                </Title>
                              </Flex>
                            </Flex>

                            {/* Servicios de optimización (subfilas) */}
                            {serviceRows.map(
                              (svc: any, sIdx: number) => (
                                <Flex
                                  key={`svc-${variant}-${row.item.orderItemId}-${sIdx}`}
                                  align="center"
                                  justify="space-between"
                                  style={{ marginTop: 12 }}
                                >
                                  <Flex
                                    align="center"
                                    justify="end"
                                    style={{ width: 102 }}
                                  >
                                    <Larrow style={{ marginRight: 10 }} />
                                    <img
                                      src={svc.icon}
                                      style={{
                                        width: 47,
                                        height: 47,
                                        objectFit: "cover",
                                        borderRadius: 4,
                                      }}
                                      alt={svc.label}
                                    />
                                  </Flex>
                                  <Flex
                                    vertical
                                    style={{ flex: 1, minWidth: 0 }}
                                  >
                                    <Text strong>{svc.label}</Text>
                                  </Flex>
                                  <Flex style={{ minWidth: 120 }}>
                                    <span />
                                  </Flex>
                                  <Flex
                                    vertical
                                    align="end"
                                    style={{ width: 120 }}
                                  >
                                    <Text strong>{svc.qty}</Text>
                                  </Flex>
                                  <Flex
                                    vertical
                                    align="end"
                                    style={{ minWidth: 100 }}
                                  >
                                    <Title
                                      style={{ margin: 0 }}
                                      level={5}
                                    >
                                      {formatPrice(
                                        cents(
                                          getUnitFinalInclFromDescription(
                                            svc.item,
                                          ) * svc.qty,
                                        ),
                                      )}
                                    </Title>
                                  </Flex>
                                </Flex>
                              ),
                            )}
                          </Flex>
                        );
                      };

                      // Fila de envío (Fleet/Truck) — image + name + price.
                      const renderShippingRow = (
                        variant: "cancelled" | "kept",
                      ) => {
                        const shippingName =
                          nombreDeEntrega(
                            shipment?.metadata?.name || effectiveMeta?.name,
                          ) ||
                          shipment?.deliveryMethod ||
                          "Shipping";
                        const shippingIncl =
                          Number(shipment?.priceInclTax) || 0;
                        return (
                          <Flex
                            align="center"
                            justify="space-between"
                            className="fila-producto"
                            style={{
                              borderBottom: "1px solid #f0f0f0",
                            }}
                          >
                            {/* Imagen y nombre juntos, a 16px (Diseño, 2026-10-19). */}
                            <Flex gap={16} align="center" style={{ flex: 1, minWidth: 0 }}>
                            <Flex gap={12} align="center">
                              <img
                                src={envioImage}
                                style={{
                                  width: 90,
                                  objectFit: "cover",
                                }}
                                alt="Shipping"
                              />
                            </Flex>
                            <Flex vertical style={{ flex: 1, minWidth: 0 }}>
                              <Text strong>{shippingName}</Text>
                            </Flex>
                            </Flex>
                            <Flex
                              style={{ minWidth: 120 }}
                              align="center"
                            >
                              <DiscountPrice shipment={shipment} />
                            </Flex>
                            <Flex
                              vertical
                              align="end"
                              style={{ width: 120 }}
                            >
                              {variant === "cancelled" ? (
                                <Flex align="center" gap={6}>
                                  <Text
                                    style={{
                                      color: "#ff4d4f",
                                    }}
                                  >
                                    {t("orders.shipment.cancel")}
                                  </Text>
                                  <Text strong>1</Text>
                                </Flex>
                              ) : (
                                <Text>1</Text>
                              )}
                            </Flex>
                            <Flex
                              align="end"
                              style={{ minWidth: 100 }}
                            >
                              <Title
                                style={{
                                  margin: 0,
                                  textAlign: "right",
                                  width: "100%",
                                }}
                                level={5}
                              >
                                {shippingIncl === 0
                                  ? t("orders.shipment.free")
                                  : formatPrice(shippingIncl)}
                              </Title>
                            </Flex>
                          </Flex>
                        );
                      };

                      // Totales por bloque.
                      const cancelledTotal = cents(
                        toCancel.reduce((acc: number, r: any) => {
                          const product = productsMap?.[r.item.orderItemId];
                          const unit = product
                            ? getUnitFinalInclFromDescription(product)
                            : 0;
                          return acc + unit * r.qty;
                        }, 0) +
                          (shippingInCancelled
                            ? Number(shipment?.priceInclTax) || 0
                            : 0),
                      );
                      const keptTotal = cents(
                        toKeep.reduce((acc: number, r: any) => {
                          const product = productsMap?.[r.item.orderItemId];
                          const unit = product
                            ? getUnitFinalInclFromDescription(product)
                            : 0;
                          return acc + unit * r.qty;
                        }, 0) +
                          // shipping queda en kept solo si NO fue marcado para cancel
                          (!shippingInCancelled && toKeep.length > 0
                            ? Number(shipment?.priceInclTax) || 0
                            : 0),
                      );

                      const TotalRow = ({ amount }: { amount: number }) => (
                        <Flex
                          style={{
                            marginTop: 8,
                            backgroundColor: "#DAE8E0",
                            padding: "8px 16px",
                            borderRadius: "8px",
                          }}
                          justify="end"
                          align="baseline"
                          gap={60}
                        >
                          <Title level={5} style={{ margin: 0 }}>
                            Total
                          </Title>
                          <Title level={5} style={{ margin: 0 }}>
                            {formatPrice(amount)}
                          </Title>
                        </Flex>
                      );

                      return (
                        // Todos los espacios en 8px (Diseño, 2026-10-19).
                        <Flex vertical gap={8} style={{ marginTop: 8 }}>
                          <Card
                            className="tarjeta-productos"
                            style={{
                              background: "#fff2f0" /* colorErrorBg */,
                            }}
                            title={t("orders.shipment.productsToBeCancelled")}
                          >
                            {/* Envío en el bloque cancelled si fue marcado
                                explícitamente o si es full cancel. */}
                            {shippingInCancelled &&
                              renderShippingRow("cancelled")}
                            {toCancel.map((row: any, idx: number) =>
                              renderProductRow(
                                row,
                                idx,
                                "cancelled",
                                toCancel.length,
                              ),
                            )}
                            <TotalRow amount={cancelledTotal} />
                          </Card>
                          {toKeep.length > 0 && (
                            <Card
                              className="tarjeta-productos"
                              style={{
                                background:
                                  "rgba(0, 0, 0, 0.06)" /* colorFillContent */,
                              }}
                              title={t("orders.shipment.productsThatAreKept")}
                            >
                              {/* Envío en kept solo si NO fue marcado para cancel */}
                              {!shippingInCancelled &&
                                renderShippingRow("kept")}
                              {toKeep.map((row: any, idx: number) =>
                                renderProductRow(
                                  row,
                                  idx,
                                  "kept",
                                  toKeep.length,
                                ),
                              )}
                              <TotalRow amount={keptTotal} />
                            </Card>
                          )}
                        </Flex>
                      );
                    })()}

                    {/* MOVE PREVIEW (2-bloques) — replica visual del cancel
                        preview, ahora mostrando los productos a mover según
                        metadata.moveRequest. Bloque "moving" en amarillo
                        suave + bloque "kept" en gris. */}
                    {shipment?.status === "pending_move" && (() => {
                      const moveReq =
                        (shipment?.metadata as any)?.moveRequest;
                      if (!moveReq?.items?.length) return null;

                      const sourceItems = shipment?.items ?? [];
                      const moveByItemId = new Map<number, number>();
                      (moveReq.items as any[]).forEach((it) => {
                        moveByItemId.set(
                          Number(it.itemId),
                          Number(it.qty) || 0,
                        );
                      });

                      const toMove = sourceItems
                        .map((it: any) => {
                          const orderItemId = Number(
                            it.orderItemId ?? it.itemId,
                          );
                          return {
                            item: it,
                            qty: moveByItemId.get(orderItemId) || 0,
                          };
                        })
                        .filter((x: any) => x.qty > 0);
                      const toKeep = sourceItems
                        .map((it: any) => {
                          const orderItemId = Number(
                            it.orderItemId ?? it.itemId,
                          );
                          const total = Number(it.qty) || 0;
                          const moveQty = moveByItemId.get(orderItemId) || 0;
                          return { item: it, qty: Math.max(0, total - moveQty) };
                        })
                        .filter((x: any) => x.qty > 0);

                      const renderRow = (
                        row: { item: any; qty: number },
                        index: number,
                        variant: "moving" | "kept",
                        listLength: number,
                      ) => {
                        const product = productsMap?.[row.item.orderItemId];
                        const linked = product?.linkedProduct;
                        const imgSrc =
                          linked?.variations?.[0]?.mainCatalog?.image ||
                          product?.variations?.[0]?.mainCatalog?.image ||
                          noImage;
                        const displayName =
                          linked?.name || product?.name || "-";
                        const displayCatalogName =
                          linked?.variations?.[0]?.mainCatalog?.name ||
                          product?.variations?.[0]?.mainCatalog?.name ||
                          "-";
                        const displaySku = product?.sellerSku || "-";
                        const unitFinalIncl = product
                          ? getUnitFinalInclFromDescription(product)
                          : 0;
                        const lineTotalIncl = cents(unitFinalIncl * row.qty);

                        return (
                          <Flex
                            key={`move-${variant}-${row.item.orderItemId}`}
                            align="center"
                            justify="space-between"
                            className="fila-producto"
                            style={{
                              borderBottom:
                                index !== listLength - 1
                                  ? "1px solid #f0f0f0"
                                  : "",
                            }}
                          >
                            {/* Imagen y nombre juntos, a 16px (Diseño, 2026-10-19). */}
                            <Flex gap={16} align="center" style={{ flex: 1, minWidth: 0 }}>
                            <Flex gap={12} align="center">
                              <img
                                src={imgSrc}
                                style={{ width: 90, objectFit: "cover" }}
                                alt={displayName}
                              />
                            </Flex>
                            <Flex vertical style={{ flex: 1, minWidth: 0 }}>
                              <Text strong style={{ marginBottom: 16 }}>
                                {displayName}
                              </Text>
                              <Text italic>{displayCatalogName}</Text>
                              <Text>Seller SKU {displaySku}</Text>
                            </Flex>
                            </Flex>
                            <Flex
                              vertical
                              justify="center"
                              align="end"
                              style={{ minWidth: 120 }}
                            >
                              <DiscountPrice item={product} />
                            </Flex>
                            <Flex
                              vertical
                              align="end"
                              style={{ width: 120 }}
                            >
                              {variant === "moving" ? (
                                <Flex align="center" gap={6}>
                                  <Text style={{ color: "#d48806" }}>
                                    {t("orders.shipment.move")}
                                  </Text>
                                  <Text strong>{row.qty}</Text>
                                </Flex>
                              ) : (
                                <Text>{row.qty}</Text>
                              )}
                            </Flex>
                            <Flex
                              vertical
                              align="end"
                              style={{ minWidth: 100 }}
                            >
                              <Title style={{ margin: 0 }} level={5}>
                                {formatPrice(lineTotalIncl)}
                              </Title>
                            </Flex>
                          </Flex>
                        );
                      };

                      const computeTotal = (rows: { item: any; qty: number }[]) =>
                        cents(
                          rows.reduce((acc, r) => {
                            const product = productsMap?.[r.item.orderItemId];
                            const unit = product
                              ? getUnitFinalInclFromDescription(product)
                              : 0;
                            return acc + unit * r.qty;
                          }, 0),
                        );
                      const movingTotal = computeTotal(toMove);
                      const keptTotal = computeTotal(toKeep);

                      const TotalRow = ({ amount }: { amount: number }) => (
                        <Flex
                          style={{
                            marginTop: 8,
                            backgroundColor: "#DAE8E0",
                            padding: "8px 16px",
                            borderRadius: "8px",
                          }}
                          justify="end"
                          align="baseline"
                          gap={60}
                        >
                          <Title level={5} style={{ margin: 0 }}>
                            Total
                          </Title>
                          <Title level={5} style={{ margin: 0 }}>
                            {formatPrice(amount)}
                          </Title>
                        </Flex>
                      );

                      // Resolver nombre del target para subtítulo.
                      let targetLabel = "nueva entrega";
                      if (moveReq.target?.mode === "existing") {
                        const tgt = (allShipments ?? []).find(
                          (s: any) =>
                            s?.shipmentId === moveReq.target?.shipmentId,
                        );
                        targetLabel =
                          nombreDeEntrega(tgt?.metadata?.name) ||
                          tgt?.deliveryMethod ||
                          `Delivery #${moveReq.target?.shipmentId}`;
                      }

                      return (
                        // Todos los espacios en 8px (Diseño, 2026-10-19).
                        <Flex vertical gap={8} style={{ marginTop: 8 }}>
                          <Card
                            className="tarjeta-productos tarjeta-encabezado-32"
                            style={{ background: "#fffbe6" }}
                            title={t("orders.shipment.productsToBeMoving", {
                              target: targetLabel,
                            })}
                          >
                            {toMove.map((row: any, idx: number) =>
                              renderRow(row, idx, "moving", toMove.length),
                            )}
                            <TotalRow amount={movingTotal} />
                          </Card>
                          {toKeep.length > 0 && (
                            <Card
                              className="tarjeta-productos tarjeta-encabezado-32"
                              style={{
                                background: "rgba(0, 0, 0, 0.06)",
                              }}
                              title={t("orders.shipment.productsThatAreKept")}
                            >
                              {toKeep.map((row: any, idx: number) =>
                                renderRow(row, idx, "kept", toKeep.length),
                              )}
                              <TotalRow amount={keptTotal} />
                            </Card>
                          )}
                        </Flex>
                      );
                    })()}

                    {/* CANCEL CONFIRMATION MODAL — usamos Modal.confirm()
                        imperativo desde el onClick del botón. Antes era
                        `<Modal open={...}>` declarativo pero el state se
                        quedaba stuck (modal montaba pero no rendering
                        visible) — antd 5 con la combinación de portal
                        adentro de la Collapse hizo conflicto. */}

                    {/* TOTAL — se oculta en el preview de cancelación parcial:
                        ahí cada bloque (cancelled / kept) ya muestra su propio
                        total y el total de la entrega sobra. */}
                    {!(isSelectMode && showCancelPreview) && (
                      <Flex
                        style={{
                          marginTop: 8,
                          backgroundColor: "#DAE8E0",
                          padding: "8px 16px",
                          borderRadius: "8px",
                        }}
                        justify="end"
                        align="baseline"
                        gap={60}
                      >
                        <Title level={5} style={{ margin: 0 }}>
                          Total
                        </Title>
                        <Title level={5} style={{ margin: 0 }}>
                          {formatPrice(shipmentTotalInclTax)}
                        </Title>
                      </Flex>
                    )}
                  </>
                )}
                <Image
                  style={{ display: "none" }}
                  preview={{
                    visible: previewOpen,
                    onVisibleChange: (visible) => setPreviewOpen(visible),
                    afterOpenChange: (visible) =>
                      !visible && setPreviewImage(""),
                  }}
                  src={previewImage}
                />

                {/* Modal para previsualizar PDFs */}
                <Modal
                  open={previewPdfOpen}
                  title="Vista previa del PDF"
                  footer={
                    <Flex justify="end" align="center">
                      <Button
                        type="primary"
                        onClick={() => {
                          const link = document.createElement("a");
                          link.href = previewPdf;
                          link.download = previewFilename;
                          link.click();
                        }}
                      >
                        Descargar PDF
                      </Button>
                    </Flex>
                  }
                  onCancel={() => {
                    setPreviewPdfOpen(false);
                    setPreviewPdf("");
                    setPreviewFilename("");
                    setNumPages(null);
                  }}
                  width={900}
                  centered
                  destroyOnClose
                  styles={{ body: { maxHeight: "75vh", overflowY: "auto", padding: "20px" } }}
                >
                  <Document
                    key={previewPdf}
                    file={previewPdf}
                    onLoadSuccess={({ numPages }) => setNumPages(numPages)}
                    onLoadError={(error) => console.error("Error loading PDF:", error)}
                    options={pdfOptions}
                    loading={
                      <Flex justify="center" align="center" style={{ padding: "40px" }}>
                        <Text>Cargando PDF...</Text>
                      </Flex>
                    }
                    error={
                      <Flex
                        vertical
                        align="center"
                        justify="center"
                        style={{ padding: "40px" }}
                      >
                        <Text type="danger">
                          No se puede mostrar el PDF.
                        </Text>
                        <Button
                          type="primary"
                          style={{ marginTop: "16px" }}
                          onClick={() => {
                            const link = document.createElement("a");
                            link.href = previewPdf;
                            link.download = "documento.pdf";
                            link.click();
                          }}
                        >
                          Descargar PDF
                        </Button>
                      </Flex>
                    }
                  >
                    {numPages && Array.from(new Array(numPages), (el, index) => (
                      <Page
                        key={`page_${index + 1}`}
                        pageNumber={index + 1}
                        width={850}
                        renderTextLayer={false}
                        renderAnnotationLayer={false}
                      />
                    ))}
                  </Document>
                </Modal>
              </Flex>
            ),
          },
        ]}
      />
    </>
  );
};
